import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactElement } from 'react';
import { vignetteFaktor, abschnitte as abschnitteVon, angleichen, angleichenRuhig, filmStuecke, passenderFilm, stueckBei, noetigerVorrat, pufferAbgelaufen, pufferBedarf, pufferSatt, sprungErlaubt, sprungZiel, suchdauerNeu, resolveBrandColor, sortedClips, wrapText, betonteStuecke, zeitleistenZeit, type BrandKit, type Clip, type TextStyle, type Timeline, timelineDuration, transformAt, darfAnlaufen, anpassen, taktStufe, tonStufe, lageSchluessel, einpassen, ebenenKasten, quellZeit, rampeVon, UEBERGAENGE_MIT_FORM, MISCH_CSS} from '@schnitt/shared';
import { useEditor } from '../store';
import { miss, zaehleBild } from '../messung';
import { meldeFreischalter, vermerkeFreischalten } from '../ton';
import * as tonkette from '../tonkette';
import { LeererZustand } from './LeererZustand';
import { FarbraederFilter, GammaFilter, KanteFilter, LookFilter, gammaOf, lutOf, raederOf } from '../looks';
import { api, type AssetView } from '../api';
import { audibleClips, baseTrack, cssFilter, farbwerteVon, visibleBase, visibleOverlays, vignetteAmount, type LaufenderUebergang, type VisibleBase } from '../playback';
import { svgFormen, svgLage, rasterLinien } from '@schnitt/shared';
import { markenKasten, markenLage, markenVersatz, type MarkenKasten } from '@schnitt/shared';
import { setzeTaktgeber } from '../takt';
import { taktLaeuft, type TaktStand } from '@schnitt/shared';
import { CanvasHandles } from './CanvasHandles';
import { ContextMenu } from './ContextMenu';
import type { SheetName } from './Toolbar';
import { clipMenuItems } from '../clipMenu';
import { GL_GEWUENSCHT, Maler, leinwandBericht, messungZurueck, type Ebene as GlEbene } from '../glmaler';
import { uebergangsMaskenStil } from '../uebergangsMaskeCss';
import { useTransportLage } from '../transportlage';
import { setFilmLaeuft } from '../filmwahl';
import { maleMarke, maleText, markenSchluessel, textSchluessel } from '../textmaler';
import { bildNummer, bildtakt, takte } from '../bildtakt';
import { messungEndet, messungStartet, messungWartet } from '../messwerte';
import { bildWartenBeginnt, bildWartenEndet, pufferBeginnt, pufferEndet, pufferGenug } from '../puffer';
import { ausDemRam, inDenRam, ramLeeren } from '../ramvorrat';

/**
 * The preview. Two pooled <video> elements play the base track so a transition
 * can cross-fade, overlays are DOM on top, and a pool of <audio> elements
 * carries anything on an audio track.
 *
 * Beim Stehen und beim Suchen gibt der Kopf die Medienzeit vor. Läuft die
 * Wiedergabe, ist es umgekehrt: das sichtbare Video gibt den Takt an (siehe
 * takt.ts), alles andere folgt ihm. Sonst laufen Wanduhr und Dekoder
 * auseinander, und der Ausgleich per Sprung lässt das Bild auf dem Telefon
 * stehen.
 */

/** So lange muss der Finger liegen bleiben, damit es ein Druck ist und kein Zug. */
/**
 * Die vier Mischungen als Zahl für den Shader (7.132) -- in derselben
 * Reihenfolge, die `MISCH_CSS` als Wort schreibt. Zwei Listen, eine Ordnung:
 * ändert sich eine, fällt die andere auf.
 */
const MISCH_GL: Record<string, number> = { normal: 0, multiply: 1, screen: 2, softlight: 3 };

const LANGER_DRUCK = 500;

/** Beim Stehen: ab hier lohnt der Sprung an die gesuchte Stelle. */
const SEEK_TOLERANCE = 0.25;

/**
 * **Im Stehen zeigt das Bild, wo der Kopf steht -- auf das Bild genau.**
 *
 * Sascha, 23. September: „Leider kann ich nicht sehen was ich am Play Marker
 * sehe wenn ich Spule“. Gemessen: der Kopf geht beim Spulen um 0,2 s weiter,
 * und das Bild bleibt stehen, auch eine Sekunde später noch. Gesucht wurde
 * erst ab einer Viertelsekunde Abstand -- beim feinen Spulen, Bild für Bild,
 * blieb es also bis zu sechs Bilder hinter dem Marker zurück, genau dort, wo
 * man einen Schnitt setzen will.
 *
 * Gesucht wird deshalb, **sobald sich das Ziel ändert**, und nicht, sobald
 * der Abstand gross genug ist. Verglichen wird mit dem zuletzt gesetzten
 * Ziel und nicht mit `currentTime`: ein Browser, der die Zeit auf ein Bild
 * der Datei rundet, stünde sonst immer ein wenig daneben und würde bei jedem
 * Zeichnen neu suchen. Die Viertelsekunde bleibt als Netz darunter -- für
 * ein Element, das nach dem Abspielen irgendwo anders steht.
 */
/**
 * **Die Deckung eines Basisvideos im Weg ohne Leinwand** (7.157).
 *
 * Sascha, 24. September, mit Bildschirmfoto: „Habe einen Übergang eingefügt.
 * Leider ist hier erst der Übergang von einem zum anderen Video“ -- mitten in
 * der Blende zeigte die Vorschau nur das alte Video. Gemessen an drei Clips
 * mit zwei Überblendungen: die erste mischte (rgb 114,92,52 zwischen Grün und
 * Rot), die zweite zeigte bis zu ihrem Ende nur Rot.
 *
 * Die beiden Videoelemente liegen in fester Reihenfolge übereinander: Platz 1
 * über Platz 0. Die Clips der Basisspur wechseln sich auf den Plätzen ab. Also
 * liegt der kommende Clip nur jedes zweite Mal oben -- und die Rechnung ging
 * davon aus, dass er es immer tut: er bekam die Deckung `p`, der gehende blieb
 * voll. Liegt der kommende unten, deckt der gehende ihn bis zum Schluss zu.
 *
 * Umgestellt wird deshalb nicht die Reihenfolge (ein Video im Dokument
 * umzuhängen hält es auf dem iPhone womöglich an), sondern die Deckung. Liegt
 * der kommende unten, deckt *er* voll, und der gehende darüber wird mit
 * `1 − p` durchsichtig. Das ergibt dieselbe Mischung: `p·neu + (1−p)·alt`.
 *
 * Die Leinwand braucht das nicht: sie malt ihre Ebenen in der Reihenfolge
 * der Spur, der kommende Clip also immer zuletzt.
 */
function deckungImStapel(entry: VisibleBase, base: readonly VisibleBase[]): number {
  const b = entry.blende;
  if (!b) return entry.opacity;
  const partner = base.find((x) => x !== entry && x.blende && x.blende.rolle !== b.rolle);
  if (!partner) return entry.opacity;
  const kommend = b.rolle === 'kommt' ? entry : partner;
  if (kommend.slot === 1) return entry.opacity;
  return b.rolle === 'kommt' ? b.ohne : b.ohne * (1 - b.p);
}

/**
 * **Ein Übergang mit Form im Weg ohne Leinwand** (7.161): welche Deckung
 * das Element bekommt und ob es die Maske trägt.
 *
 * Die Leinwand legt den kommenden Clip mit der Maske `m` über den gehenden.
 * Hier liegen die beiden Elemente in fester Reihenfolge (siehe
 * `deckungImStapel`). Liegt der kommende oben, trägt er `m`. Liegt er unten,
 * deckt er voll, und der gehende darüber trägt `1 − m` -- das ergibt
 * dasselbe Bild. Beide decken dabei mit ihrer Deckung *ohne* den Übergang:
 * die Kante macht ihn, nicht die Durchsichtigkeit.
 */
function formImStapel(
  entry: VisibleBase,
  base: readonly VisibleBase[],
): { deckung: number; maske?: { art: NonNullable<VisibleBase['uebergang']>['art']; p: number; umgekehrt: boolean } } | null {
  const kommend = base.find((b) => b.uebergang);
  const u = kommend?.uebergang;
  if (!kommend || !u) return null;
  if (entry === kommend) {
    return kommend.slot === 1 ? { deckung: u.deckung, maske: { art: u.art, p: u.p, umgekehrt: false } } : { deckung: u.deckung };
  }
  if (entry.blende?.rolle !== 'geht') return null;
  return kommend.slot === 1 ? { deckung: entry.opacity } : { deckung: entry.opacity, maske: { art: u.art, p: u.p, umgekehrt: true } };
}

/**
 * **Die Vignette ohne Leinwand, nach derselben Formel** (7.172).
 *
 * Bis zum 25. September lag auf der Basisspur ein Verlauf „durchsichtig bis
 * 40 %, dann schwarz“ -- eine Schätzung --, und eine Farbfläche auf einer
 * oberen Spur bekam gar keine: im Wege-Vergleich 161 von 255 daneben, auf
 * drei Vierteln des Bildes. Jetzt schwarz mit der Deckung `1 − Faktor`, der
 * Faktor aus `vignetteFaktor` wie im Shader und im Film. Gemessen wird am
 * leinwandförmigen Rahmen, der hier das Element selbst ist; `circle
 * farthest-corner` hat die halbe Diagonale zum Radius, und genau durch die
 * teilt die Formel. So bleibt es ein Verlauf -- billig auf dem Telefon.
 */
function vignetteVerlauf(staerke: number): string {
  const stufen: string[] = [];
  const n = 24;
  for (let i = 0; i <= n; i++) {
    const d = i / n;
    const k = vignetteFaktor(d, d, staerke, 1);
    stufen.push(`rgba(0,0,0,${(1 - k).toFixed(4)}) ${(d * 100).toFixed(2)}%`);
  }
  return `radial-gradient(circle farthest-corner at 50% 50%, ${stufen.join(', ')})`;
}

function stelleImStehen(element: HTMLMediaElement, ziel: number): void {
  const zuletzt = Number(element.dataset.gestellt);
  const neu = !(Math.abs(zuletzt - ziel) < 1e-4);
  if (neu || Math.abs(element.currentTime - ziel) > SEEK_TOLERANCE) {
    element.currentTime = ziel;
    element.dataset.gestellt = String(ziel);
  }
}

/**
 * So viele Sekunden im Voraus wird geladen, was als Nächstes zu sehen ist.
 * Drei reichen für eine Vorschau-Datei über eine gewöhnliche Leitung, und sie
 * sind kurz genug, dass beim Öffnen eines Projekts nicht alles auf einmal
 * angefordert wird.
 */
const VORLAUF = 3;
/**
 * Für die *Basisspur* mehr (7.69q). Sascha, 10. September: „ganz oft wenn ich
 * play drücke, hält es kurz vor den videos kurz an.“ Drei Sekunden reichen
 * für das Holen der Datei, aber nicht immer für das, was danach kommt:
 * öffnen, die Stelle suchen, das erste Bild dekodieren. Die zweite
 * Bildfläche steht ohnehin da und ist leer -- sie früher zu füllen kostet
 * keinen zusätzlichen Dekoder, anders als bei den Ebenen (7.69p), wo jedes
 * Element einen eigenen mitbringt. Deshalb gilt die grössere Zahl nur hier.
 */
const VORLAUF_BASIS = 8;
/** So weit voraus bekommen Videoebenen ihr Element, damit die Berührung es freischalten kann (7.69m). */
const VORLAUF_EBENEN = 60;

/**
 * Alles, was die Leinwand aus dem Abspielkopf ableitet, als zwei
 * Zeichenketten: was man sieht, und was man hört. Gleiche Kette, gleiches
 * Bild -- und dann darf der Durchlauf ausbleiben. Getrennt, damit hinterher
 * dasteht, *warum* neu gezeichnet wurde; „66-mal“ allein sagt nichts.
 *
 * Die Entscheidungen darin -- Takt, Tonstufe, Lage -- stehen in
 * `bildtakt.ts` und sind dort geprüft.
 *
 * Beim Stehen wird nicht gespart: dann *ist* der Kopf die Bedienung (ziehen,
 * suchen, Bild für Bild springen), und jede Zusammenfassung wäre ein Ruckeln
 * genau dort, wo man hinsieht.
 */
function bildSchluessel(s: ReturnType<typeof useEditor.getState>): { bild: string; ton: string } {
  const kopf = s.playhead;
  if (!s.playing) return { bild: `steht ${kopf}`, ton: '' };
  const bild: string[] = [];
  for (const b of visibleBase(s.timeline, kopf)) {
    bild.push(`b${b.slot} ${b.clip.id} ${b.opacity.toFixed(3)} ${taktStufe(b.mediaTime)} ${lage(b.clip, kopf)}`);
  }
  for (const o of visibleOverlays(s.timeline, kopf)) {
    bild.push(`e ${o.clip.id} ${o.opacity.toFixed(3)} ${taktStufe(o.mediaTime)} ${lage(o.clip, kopf)}`);
  }
  const hatTon = new Map(s.assets.map((a) => [a.id, a.hasAudio]));
  const ton: string[] = [];
  for (const a of audibleClips(s.timeline, kopf, (id) => hatTon.get(id) ?? false)) {
    ton.push(`t ${a.clip.id} ${tonStufe(a.volume)}`);
  }
  return { bild: bild.join('|'), ton: ton.join('|') };
}

/** Wo eine Ebene in diesem Augenblick steht -- Keyframes und Anflug inbegriffen. */
function lage(clip: Clip, kopf: number): string {
  return lageSchluessel(transformAt(clip, kopf - clip.start), slideOffset(clip.animation, kopf - clip.start));
}

/**
 * Der Abspielkopf, wie die Leinwand ihn braucht: er rueckt nur nach, wenn sich
 * am Bild etwas aendert. Sonst steht er still -- und mit ihm der ganze
 * Durchlauf.
 */
function useBildKopf(): number {
  const [kopf, setKopf] = useState(() => useEditor.getState().playhead);
  useEffect(() => {
    let letzter = bildSchluessel(useEditor.getState());
    return useEditor.subscribe((s) => {
      const neu = miss('bild-schluessel', () => bildSchluessel(s));
      const bildAnders = neu.bild !== letzter.bild;
      const tonAnders = neu.ton !== letzter.ton;
      if (!bildAnders && !tonAnders) return;
      // Warum die Leinwand neu zeichnet, getrennt gezaehlt. Ohne das steht am
      // Ende eine Zahl da und niemand weiss, ob das Bild sich bewegt hat oder
      // nur eine Pegelkurve gestiegen ist.
      if (bildAnders) zaehleBild('kopf-bild');
      if (tonAnders) zaehleBild('kopf-ton');
      letzter = neu;
      setKopf(s.playhead);
    });
  }, []);
  return kopf;
}

/**
 * Das Tempo nur schreiben, wenn es sich wirklich ändert.
 *
 * Diese Wirkung läuft bei jedem Bild; ein Tonelement, dessen `playbackRate`
 * sechzigmal je Sekunde einen Hauch anders gesetzt wird, klingt auf dem
 * Telefon zerhackt. (Saschas Fund vom 6. September: „mein Voiceover hakt.“)
 */
/**
 * Ein `play()`, das nicht zweimal gleichzeitig anläuft.
 *
 * Ruft man `play()` ein zweites Mal, während das erste noch lädt, bricht der
 * Browser das erste ab. Auf dem iPhone ist aber genau das erste dasjenige,
 * das aus der Berührung kam -- und nur das darf klingen. Sascha ist darüber
 * gestolpert (6. September): mitten in einen Ton spulen und dann Play
 * drücken. Dann liefen der Freischalter aus der Berührung und die Steuerung
 * aus dem nächsten Bild gegeneinander, der Berührungs-Anlauf wurde
 * abgebrochen, und übrig blieb ein `play()`, das iOS nicht erlaubt.
 *
 * `fertig` bekommt `null`, wenn es läuft, und den Fehler, wenn nicht.
 */
function starte(element: HTMLMediaElement, fertig?: (fehler: Error | null) => void, dringend = false): void {
  if (!element.paused) {
    fertig?.(null);
    return;
  }
  const zahl = (wert: string | undefined): number | null => {
    const n = wert ? Number(wert) : NaN;
    return Number.isFinite(n) ? n : null;
  };
  const seit = zahl(element.dataset.anlauf);
  // `dringend` ist der Weg aus einer Berührung heraus: dort darf es plötzlich,
  // auch wenn es eben noch abgewiesen wurde.
  const abgewiesen = dringend ? null : zahl(element.dataset.abgewiesen);
  if (!darfAnlaufen({ paused: true, seit, abgewiesen }, Date.now())) {
    // Genau der Aufruf, der den ersten abbrechen würde. Die Zahl bleibt am
    // Element stehen: sie sagt, wie oft es knapp war.
    element.dataset.doppelt = String(Number(element.dataset.doppelt ?? 0) + 1);
    return;
  }
  element.dataset.anlauf = String(Date.now());
  element.dataset.anlaeufe = String(Number(element.dataset.anlaeufe ?? 0) + 1);
  delete element.dataset.gehalten;
  delete element.dataset.abgewiesen;
  const versprechen = element.play();
  if (!versprechen) {
    delete element.dataset.anlauf;
    fertig?.(null);
    return;
  }
  versprechen
    .then(() => {
      delete element.dataset.anlauf;
      // Und der Grund von vorhin geht weg. Er beschreibt den letzten Ausgang,
      // und ein gelungener Anlauf ist auch einer. Blieb er stehen, trug ein
      // Element, das längst lief, weiter „AbortError“ -- und der Befund vom
      // Telefon (7.69m) meldete einen Fehler, den es nicht mehr gab. Ein
      // Abbruch ist dort besonders häufig, weil die App ihn selbst auslöst:
      // ein `pause()` bricht ein noch ladendes `play()` ab, und genau das ist
      // laut der Stelle darüber „keine Absage“.
      delete element.dataset.grund;
      fertig?.(null);
    })
    .catch((fehler: unknown) => {
      delete element.dataset.anlauf;
      element.dataset.abgewiesen = String(Date.now());
      // Der Grund bleibt am Element stehen: im Befund vom Telefon ist er das
      // eine Wort, das „bleibt stehen“ von „darf nicht“ unterscheidet (7.69m).
      element.dataset.grund = fehler instanceof Error ? fehler.name : String(fehler);
      fertig?.(fehler instanceof Error ? fehler : new Error(String(fehler)));
    });
}

/**
 * Anhalten -- und den laufenden Anlauf dabei vergessen.
 *
 * Ein `pause()` bricht ein `play()` ab, das noch lädt. Die Absage dazu kommt
 * auf manchen Geräten nie, und wer auf sie wartet, wartet für immer: genau
 * daran ist Saschas Wiedergabe hängengeblieben (einmal abspielen, Pause, und
 * danach ging nichts mehr). Also wird der Riegel hier weggenommen, statt auf
 * eine Nachricht zu hoffen.
 */
function halte(element: HTMLMediaElement, warum?: string): void {
  delete element.dataset.anlauf;
  // Wer angehalten hat, bleibt am Element stehen (7.137): ein `AbortError`
  // heisst nur „während des Anlaufs angehalten“. Ob das die Pause war, der
  // Film, der den Ton übernimmt, oder die Tonkette, sagt erst dieses Wort.
  if (!element.paused) element.dataset.gehalten = warum ?? 'anderswo';
  if (!element.paused) element.pause();
}

function setzeTempo(element: HTMLMediaElement, rate: number): void {
  if (Math.abs(element.playbackRate - rate) > 0.004) element.playbackRate = rate;
}

/**
 * Der Sprung eines folgenden Videos, mit Disziplin (7.69l). Was Saschas
 * iPhone am 9. September meldete: mehr als ein Sprung je Sekunde auf der
 * Videoebene, fünf dekodierte Bilder in der ganzen Wiedergabe. Jeder Sprung
 * setzte die Dekodierung neu auf, das Suchen dauerte länger als die halbe
 * Sekunde, ab der gesprungen wird -- und so kam ein Bild je Sprung.
 * Deshalb: nicht während des Suchens, nicht bevor dekodiert ist, nicht kurz
 * nach dem letzten -- und dann dorthin, wo der Kopf nach dem Suchen sein wird.
 * Liefert, ob gesprungen wurde.
 */
function springe(element: HTMLMediaElement, jetzt: number, speed: number, name: string): boolean {
  const jetztMs = performance.now();
  const letzter = element.dataset.sprung ? Number(element.dataset.sprung) : NaN;
  const stand = { suchend: element.seeking, bereit: element.readyState, letzterSprung: Number.isFinite(letzter) ? letzter : null };
  if (!sprungErlaubt(stand, jetztMs)) {
    zaehleBild(`${name}-vertagt`);
    return false;
  }
  zaehleBild(name);
  element.dataset.sprung = String(jetztMs);
  element.currentTime = sprungZiel(jetzt, speed, Number(element.dataset.suchdauer ?? 0));
  return true;
}

/** Wie lange das Gerät für ein Suchen braucht -- gemessen, am Element notiert. */
function merkeSuchen(e: { currentTarget: HTMLMediaElement }): void {
  e.currentTarget.dataset.suchtSeit = String(performance.now());
}
function merkeGesucht(e: { currentTarget: HTMLMediaElement }): void {
  const v = e.currentTarget;
  const seit = Number(v.dataset.suchtSeit ?? NaN);
  delete v.dataset.suchtSeit;
  if (!Number.isFinite(seit)) return;
  const bisher = v.dataset.suchdauer ? Number(v.dataset.suchdauer) : null;
  v.dataset.suchdauer = String(Math.round(suchdauerNeu(Number.isFinite(bisher ?? NaN) ? bisher : null, performance.now() - seit)));
}

/** Welche Videoebene gerade den Takt angibt, wenn die Basisspur keinen hat (7.69l). */
let taktendeEbene: string | null = null;
/** Wer zuletzt getaktet hat -- für den Durchlauf und das Melde-Blatt. */
let zuletztGetaktet = 'wanduhr';
/**
 * Wann die Meldung zuletzt erneuert wurde (7.171). Sie wird nur beim
 * Abspielen geschrieben; ohne Zeitstempel sagte sie nach dem Anhalten für
 * immer den letzten Clip -- und beim nächsten Abspielen, bevor der erste Takt
 * lief, einen, den es dort vielleicht gar nicht mehr gab.
 */
let zuletztGetaktetUm = -Infinity;


export function Preview({
  oeffneMedien,
  oeffneBlatt,
}: { oeffneMedien?: () => void; oeffneBlatt?: (blatt: SheetName) => void } = {}): ReactElement {
  zaehleBild('vorschau');
  const timeline = useEditor((s) => s.timeline);
  const assets = useEditor((s) => s.assets);
  const playhead = useBildKopf();
  const playing = useEditor((s) => s.playing);
  const setPlaying = useEditor((s) => s.setPlaying);
  const monitorMuted = useEditor((s) => s.monitorMuted);
  const brandKit = useEditor((s) => s.project?.brandKit);
  const brandLogoUrl = useEditor((s) => s.project?.brandLogoUrl ?? null);
  // 7.62: Wandert die Transportleiste nach oben, braucht die Leinwand dort
  // Platz statt unten -- die Leiste ändert ihre Grösse dabei nicht, also
  // sagt es kein ResizeObserver; die Lage selbst löst das Neu-Einpassen aus.
  const transportLage = useTransportLage();

  const videoA = useRef<HTMLVideoElement>(null);
  const videoB = useRef<HTMLVideoElement>(null);
  const audioPool = useRef(new Map<string, HTMLAudioElement>());
  /** Wo die Tonelemente im Dokument hängen -- unsichtbar, aber vorhanden. */
  const tonHalter = useRef<HTMLDivElement>(null);
  /** Welche Elemente ihr erstes `play()` aus einer Berührung schon hatten. */
  const freigeschaltet = useRef(new Set<string>());
  /**
   * Was am Element stand, als der Freischalter es angestossen hat (7.71d).
   *
   * Beim Abspielen übernimmt der Film und gibt beim Anhalten wieder ab: was
   * danach dasteht, ist ein *neues* DOM-Element, und die Vermerke des
   * Freischalters standen auf dem alten. Hier stehen sie, damit sie den
   * Austausch überleben -- sonst meldet der Gerätebefund „nicht
   * freigeschaltet“, wo alles in Ordnung ist.
   */
  const freiVermerk = useRef(new Map<string, { frei: string; freiStumm: string }>());
  /** Was in diesem Augenblick klingen soll; der Freischalter fragt danach. */
  const hoerbar = useRef(new Set<string>());
  const stage = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  /**
   * Die Leinwand, die selbst malt (7.69a). Vorerst nur die Basisspur, und nur
   * hinter `?gl=1` -- die Ebenen darüber liegen weiter als Elemente im Bild.
   */
  const glLeinwand = useRef<HTMLCanvasElement>(null);
  const maler = useRef<Maler | null>(null);
  const glMasse = useRef({ breite: 0, hoehe: 0 });
  /**
   * Das Kontextmenü auf der Leinwand (7.61).
   *
   * Sascha, 8. September: „Elemente auf der Leinwand bekommen hoffentlich auch
   * noch ein Kontextmenü“. Es ist *dasselbe* wie in der Zeitleiste -- die
   * Liste steht in `clipMenu.ts`, damit nicht zwei entstehen, die sich
   * auseinanderentwickeln.
   */
  const [menu, setMenu] = useState<{ clipId: string; x: number; y: number; at: number } | null>(null);
  const druck = useRef<{ x: number; y: number; timer: number; clipId: string } | null>(null);
  const selection = useEditor((s) => s.selection);
  const rasterWahl = useEditor((s) => s.raster);
  const select = useEditor((s) => s.select);
  const setGriff = useEditor((s) => s.setGriff);
  const dispatch = useEditor((s) => s.dispatch);
  const refreshAssets = useEditor((s) => s.refreshAssets);
  const toast = useEditor((s) => s.toast);
  const setMultiSelect = useEditor((s) => s.setMultiSelect);
  const setPlayheadStore = useEditor((s) => s.setPlayhead);
  const setErsetzen = useEditor((s) => s.setErsetzen);
  const setClipName = useEditor((s) => s.setClipUmbenennen);
  const setAngaben = useEditor((s) => s.setClipAngaben);
  const setHinweisAnker = useEditor((s) => s.setHinweisAnker);
  const role = useEditor((s) => s.role);
  // Fuer das Kontextmenue auf der Leinwand: „Tonkurve ausklappen“ steht in
  // derselben Liste wie in der Zeitleiste (7.65a).
  const tonkurven = useEditor((s) => s.tonkurven);
  const tonkurveUmschalten = useEditor((s) => s.tonkurveUmschalten);

  const assetById = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);
  const [mediaError, setMediaError] = useState<string | null>(null);
  // The proxy's own size, from the element once it has loaded: the asset row
  // knows the coded size, which a phone recording stores turned on its side.
  const [videoDims, setVideoDims] = useState<Record<number, { w: number; h: number }>>({});
  /**
   * Der vorgerechnete Film (7.71). Passt einer zu dieser Fassung des Schnitts
   * und ist er gewählt, spielt die Vorschau *ihn* -- ein Videoelement statt
   * der Basisflächen, aller Ebenen und aller Tonelemente. Auf dem Telefon ist
   * das der Unterschied zwischen einem Dekoder und sechs.
   */
  const renders = useEditor((s) => s.renders);
  const rev = useEditor((s) => s.rev);
  /**
   * Die Abschnitte des Schnitts und die Stücke, die davon gerechnet sind
   * (7.71b). Wo eines liegt, spielt es; wo keines liegt, spielen die Ebenen
   * wie eh und je -- eine halb gerechnete Vorschau ist besser als keine.
   *
   * Ohne Knopf und ohne Modus (7.71d). Sascha, 10. September: „Können wir das
   * nicht intelligenter und ohne knopf machen?“ -- ein Schalter, der zwischen
   * zwei Wegen zum selben Bild umschaltet, ist eine Frage, die niemand
   * beantworten will.
   *
   * **Der Film gehört dem Abspielen, die Ebenen dem Schneiden.** Beim
   * Abspielen zählt, dass es flüssig läuft: ein Video statt sechs. Im Stehen
   * zählt, dass man anfassen kann -- eine Ebene antippen, den Rahmen ziehen,
   * einen Keyframe setzen --, und dafür müssen die Ebenen dastehen. Also
   * läuft der Film, während der Kopf läuft, und beim Anhalten übernehmen
   * wieder die Ebenen.
   */
  const abschnitte = useMemo(() => abschnitteVon(timeline), [timeline]);
  const stuecke = useMemo(
    () => filmStuecke(renders, abschnitte, passenderFilm(renders, rev), timelineDuration(timeline)),
    [renders, abschnitte, rev, timeline],
  );
  const filmA = useRef<HTMLVideoElement>(null);
  const filmB = useRef<HTMLVideoElement>(null);
  /**
   * **Der Film steht bereit, bevor er gebraucht wird** (7.239). Sascha, 12. Oktober,
   * am iPhone: „Beim Ende vom overlay. Stand dann nur noch ‚laden‘ dran“ --
   * und davor eine schwarze Vorschau. Die Messwerte: die Wiedergabe wartete
   * auf den Film (`bereit=1`, nichts geladen, 7,6 s „Lädt…“), und einmal
   * wies das Gerät sein Abspielen ab (`NotAllowedError`). Die beiden
   * Elemente entstanden erst, wenn ein Stück an der Reihe war: neu, ohne
   * Datei, nie aus einer Berührung angestossen.
   *
   * Jetzt stehen sie da, sobald es Stücke gibt, und das Stück unter dem Kopf
   * oder das nächste liegt schon darin (unten): das Tippen auf Abspielen
   * schaltet sie mit frei, und am Ende einer Ebene muss nichts erst laden.
   * Und hat das Gerät ein Element abgelehnt, übernimmt es kein Stück -- dann
   * spielen die Ebenen weiter, die ohnehin laufen, statt einer schwarzen
   * Fläche. Beim nächsten Tippen wird es wieder angestossen.
   */
  const kandidat = playing ? stueckBei(stuecke, playhead) : -1;
  const darfSpielen = (nr: number): boolean => {
    const el = (nr % 2 === 1 ? filmB : filmA).current;
    const s2 = stuecke[nr];
    return Boolean(el && s2 && el.dataset.src === s2.url && el.dataset.grund !== 'NotAllowedError');
  };
  const stueckNr = kandidat >= 0 && darfSpielen(kandidat) ? kandidat : -1;
  const film = stueckNr >= 0 ? stuecke[stueckNr]! : null;
  /** Das Element, das dieses Stück spielt -- das nächste liegt im anderen. */
  const filmRef = stueckNr % 2 === 1 ? filmB : filmA;
  const filmVorRef = stueckNr % 2 === 1 ? filmA : filmB;
  /** Das Element, das gerade wirklich den Film spielt -- oder keines. */
  const filmAktiv = useRef<HTMLVideoElement | null>(null);
  filmAktiv.current = film ? filmRef.current : null;
  setFilmLaeuft(Boolean(film));
  /**
   * Das laufende Stück als Clip gedacht: es fängt an, wo es auf der
   * Zeitleiste anfängt, läuft mit Tempo eins und ist so lang wie sein
   * Abschnitt. Damit gilt für es dieselbe Rechnung wie für jedes andere
   * Medium -- Takt, Abgleich, Puffer.
   */
  const filmClip = useMemo(
    () =>
      ({
        id: 'film',
        source: { kind: 'color', color: '#000000' },
        start: film?.von ?? 0,
        duration: Math.max(0.001, film ? film.bis - film.von : timelineDuration(timeline)),
        sourceIn: 0,
        speed: 1,
        muted: false,
        transform: { x: 0.5, y: 0.5, scale: 1, rotate: 0, opacity: 1 },
        effects: [],
        fadeIn: 0,
        fadeOut: 0,
        transitionIn: null,
        label: 'Film',
        keyframes: [],
        animation: 'none',
      }) as unknown as Clip,
    [timeline, film],
  );
  const base = miss('modell-basis', () => visibleBase(timeline, playhead));
  const overlays = miss('modell-ebenen', () => visibleOverlays(timeline, playhead));
  const audible = miss('modell-ton', () =>
    audibleClips(timeline, playhead, (id) => assetById.get(id)?.hasAudio ?? false),
  );
  const tonSpuren = useMemo(
    () => new Set(timeline.tracks.filter((t) => t.kind === 'audio').map((t) => t.id)),
    [timeline.tracks],
  );
  hoerbar.current = new Set(audible.filter((a) => tonSpuren.has(a.trackId)).map((a) => a.clip.id));
  /** Welche der beiden Bildflächen gerade wirklich etwas zeigt. */
  const bildAktiv = useRef(new Set<number>());
  bildAktiv.current = new Set(base.filter((b) => b.clip.source.kind === 'asset').map((b) => b.slot));
  /**
   * Und welche davon klingen soll. Der Freischalter braucht das *vorher*:
   * ein Video, das gleich mit Ton läuft, muss auch mit Ton angestoßen
   * werden -- sonst ist der Anlauf aus der Berührung ein stummer, und ein
   * stummer beweist auf iOS nichts (7.37d).
   */
  const bildTon = useRef(new Set<number>());
  bildTon.current = new Set(
    base.filter((b) => audible.some((a) => a.clip.id === b.clip.id)).map((b) => b.slot),
  );
  /**
   * Was als Nächstes kommt -- und deshalb jetzt schon geladen wird.
   *
   * Sascha, 8. September: „Wenn es beim Abspielen erscheint, dauert es, bis es
   * wirklich kommt. Es bleibt schwarz, obwohl der Abspieler schon weiter ist.“
   * Die Datei wurde erst in dem Augenblick angefordert, in dem der Clip zu
   * sehen sein sollte -- und eine Datei, die erst geholt, geöffnet und gesucht
   * werden muss, ist nicht in einem Bild da.
   *
   * Die beiden Bildflächen wechseln sich ab (Clip 1 auf Fläche 0, Clip 2 auf
   * Fläche 1, ...). Während der eine läuft, steht der andere leer -- genau
   * dafür gibt es ihn. Er bekommt jetzt schon die nächste Datei und springt
   * an ihren Anfang.
   */
  const naechsteBasis = useMemo(() => {
    const spur = baseTrack(timeline);
    if (!spur || spur.hidden) return null;
    const clips = sortedClips(spur);
    const index = clips.findIndex((c) => c.start > playhead);
    const clip = index >= 0 ? clips[index] : undefined;
    if (!clip || clip.source.kind !== 'asset') return null;
    // Nur, wenn es bald so weit ist: sonst lädt ein Projekt beim Öffnen alles
    // auf einmal, und das kostet auf dem Telefon mehr, als es bringt.
    if (clip.start - playhead > VORLAUF_BASIS) return null;
    return { clip, slot: (index % 2) as 0 | 1, assetId: clip.source.assetId, sourceIn: clip.sourceIn };
  }, [timeline, playhead]);

  /**
   * Dasselbe für die Ebenen darüber: was in den nächsten Sekunden auftaucht,
   * bekommt sein Element jetzt schon -- unsichtbar, angehalten, aber mit
   * Datei und an der richtigen Stelle. Wenn es dran ist, ist es dasselbe
   * Element, das dann nur noch sichtbar wird.
   */
  const baldOverlays = useMemo(() => {
    const raus: { clip: Clip; mediaTime: number; vorladen: 'auto' | 'none' }[] = [];
    const videoSpuren = timeline.tracks.filter((t) => t.kind === 'video');
    for (const spur of videoSpuren.slice(1)) {
      if (spur.hidden) continue;
      for (const clip of spur.clips) {
        if (clip.source.kind !== 'asset') continue;
        if (clip.start <= playhead || clip.start - playhead > VORLAUF_EBENEN) continue;
        // Nur der nächste lädt seine Daten; der eine weitere holt nichts
        // (7.69p). Er ist da, damit die Berührung ihn freischalten kann
        // (7.69m) -- aber ohne Daten kostet er keinen Dekoder.
        raus.push({ clip, mediaTime: clip.sourceIn, vorladen: clip.start - playhead <= VORLAUF ? 'auto' : 'none' });
      }
    }
    /**
     * Zwei, nach Nähe (7.69p). Saschas Befund vom 9. September, 23:34: vier
     * Videoelemente auf Ebenen, *alle* mit vollen Puffern (`bereit` 4, bis
     * 46 s geladen -- die vorgeladenen Dateien liegen ja im Zwischenspeicher,
     * und Safari holt sie dann gern ganz). Ein iPhone hat wenige Dekoder;
     * wer vier füttert, nimmt dem laufenden die Luft. Also einer mit Daten
     * und einer ohne, statt dreier mit.
     */
    raus.sort((a, b) => a.clip.start - b.clip.start);
    return raus.slice(0, 2);
  }, [timeline.tracks, playhead]);

  /**
   * Und die Bild-Ebenen *über* der Basisspur. Sie waren bis heute gar keine
   * Elemente: ein Video auf V2 stand als sein Standbild da und rührte sich
   * nicht (Sascha, 8. September: „das Video spielt auch am Chrome desktop
   * nicht ab auf der Leinwand. Die Thumbnails zeigen aber dass es ein Video
   * ist“ -- das Standbild *war* das Thumbnail). Sie melden sich hier an,
   * damit der Freischalter sie mitnimmt wie die beiden Basisflächen.
   */
  const fremdeBilder = useRef(new Map<string, HTMLVideoElement>());
  /**
   * Und die Standbilder -- Bild-Ebenen und die Poster von Videoebenen, deren
   * Datei noch nicht da ist. Die Leinwand malt sie von hier aus (7.69b).
   */
  const standbilder = useRef(new Map<string, HTMLImageElement>());
  /**
   * Text und Bauchbinden als Bild (7.69d): je Clip eine Zeichenfläche, ein
   * Schlüssel für das, womit sie gemalt wurde, und eine laufende Nummer, an
   * der die Grafikkarte erkennt, ob sie neu hochladen muss.
   */
  const gesetzt = useRef(new Map<string, { flaeche: HTMLCanvasElement; schluessel: string; version: number }>());
  /** Das Logo, geladen -- einmal je Adresse. */
  const logoBild = useRef<{ url: string; bild: HTMLImageElement } | null>(null);
  const meldeStandbild = useCallback((id: string, element: HTMLImageElement | null) => {
    if (element) standbilder.current.set(id, element);
    else standbilder.current.delete(id);
  }, []);
  const meldeBild = useCallback((id: string, element: HTMLVideoElement | null) => {
    if (element) fremdeBilder.current.set(id, element);
    else fremdeBilder.current.delete(id);
  }, []);
  /**
   * Die Ebenen, die erst noch kommen (7.69m). Saschas Befund vom 9. September,
   * 10:56 Uhr: die Videoebene blieb nach dem Tippen stehen -- ein Bild, kein
   * Sprung, kein Tempo, einfach pausiert -- und lief erst beim dritten Mal.
   * Ihr Element war beim Tippen noch nicht da oder nur als stummer Vorlader,
   * und ein Element, das seinen ersten Anlauf nicht aus einer Berührung
   * bekam, darf auf iOS später nicht mit Ton spielen. Deshalb melden sich
   * auch die Vorlader an, und der Freischalter stösst sie mit dem Ton an,
   * den sie später haben werden.
   */
  const vorabBilder = useRef(new Map<string, HTMLVideoElement>());
  const meldeVorab = useCallback((id: string, element: HTMLVideoElement | null) => {
    if (element) vorabBilder.current.set(id, element);
    else vorabBilder.current.delete(id);
  }, []);

  /**
   * The clip the handles belong to: exactly one selected and visible right
   * now. Auch eine Bauchbinde: sie zeichnet ihre Teile zwar selbst, liegt
   * aber als ganze Ebene im Bild -- und die lässt sich schieben, drehen und
   * größer ziehen wie jede andere. (Sascha: „mein Markeninhalt hat keinen
   * Anfasser“.)
   */
  const handled = (() => {
    if (selection.length !== 1) return undefined;
    const candidates = [...overlays.map((o) => o.clip), ...base.map((b) => b.clip)];
    const clip = candidates.find((c) => c.id === selection[0]);
    if (!clip) return undefined;
    // A video on the base track can be moved and scaled too: a landscape shot
    // in a portrait frame is reframed by dragging it, like any layer.
    return clip;
  })();
  const { width, height } = timeline.settings;

  // Fit the canvas into whatever space is left, keeping the sequence aspect.
  useEffect(() => {
    const element = stage.current;
    if (!element) return;
    // Die Glasleisten, die auf der Buehne liegen: der Transport immer, der
    // Werkzeugkasten am grossen Schirm. Die Leinwand haelt Abstand von ihnen
    // (Sascha, 7. September: "die Leinwand sollte nicht hinter dem Menue
    // liegen"). Gemessen statt geschaetzt -- die Leisten sind auf dem Telefon
    // kleiner als am Schirm, und eine Zahl im Stilblatt geht beim naechsten
    // Umbau schief.
    const leisten = () =>
      ['.transport', 'nav.toolbar:not(.cliptools)']
        .map((sel) => document.querySelector<HTMLElement>(sel))
        .filter((el): el is HTMLElement => !!el);

    /**
     * Wie viel Hoehe die Leisten der Leinwand wegnehmen.
     *
     * Nur so viel, wie sie ihr wirklich im Weg sind. Eine Leiste, die neben
     * der Leinwand steht statt darueber, kostet nichts -- und bei einem
     * Hochkant-Projekt am breiten Schirm ist genau das der Fall: die Leinwand
     * ist schmal, links und rechts liegt Platz brach. Vorher wurde stur oben
     * und unten reserviert, und das kostete auf 1440x900 hundertsechsundvierzig
     * Pixel Bild. (Sascha, 7. September: „es braucht einfach viel zu viel
     * Platz“.)
     *
     * Gerechnet wird in zwei Durchgaengen: erst die Leinwand ohne Leisten,
     * dann die Frage, ob sie sich mit einer ueberschneidet. Nur dann wird
     * abgezogen und noch einmal gerechnet.
     */
    const raum = (): { oben: number; unten: number } => {
      const box = element.getBoundingClientRect();
      const passtOhne = Math.min(box.width - 20, ((box.height - 20) * width) / height);
      const halbeBreite = Math.max(0, passtOhne) / 2;
      const mitte = box.left + box.width / 2;
      let oben = 0;
      let unten = 0;
      for (const leiste of leisten()) {
        const bar = leiste.getBoundingClientRect();
        // Nur, was wirklich auf der Buehne liegt: am Telefon steht der
        // Werkzeugkasten unter ihr und nimmt ihr nichts weg.
        if (bar.height === 0 || bar.bottom <= box.top || bar.top >= box.bottom) continue;
        // Und nur, was ihr auch in die Quere kommt. Die Leinwand steht in der
        // Mitte; was links oder rechts davon endet, stoert nicht.
        if (bar.right <= mitte - halbeBreite || bar.left >= mitte + halbeBreite) continue;
        const obenDran = bar.top - box.top < box.bottom - bar.bottom;
        if (obenDran) oben = Math.max(oben, bar.bottom - box.top + 8);
        else unten = Math.max(unten, box.bottom - bar.top + 8);
      }
      return { oben, unten };
    };

    const fit = () => {
      const { oben, unten } = raum();
      element.style.paddingTop = `${Math.max(oben, 10)}px`;
      element.style.paddingBottom = `${Math.max(unten, 10)}px`;
      const box = element.getBoundingClientRect();
      const stil = getComputedStyle(element);
      const frei = {
        width: box.width - parseFloat(stil.paddingLeft) - parseFloat(stil.paddingRight),
        height: box.height - parseFloat(stil.paddingTop) - parseFloat(stil.paddingBottom),
      };
      const canvas = element.querySelector<HTMLElement>('.canvas');
      if (canvas) {
        // **Das Innere des Kastens hat das Seitenverhältnis des Projekts,
        // nicht der Kasten samt Rand** (7.161). Bis zum 24. September bekam
        // der äussere Kasten das Verhältnis, und das Innere war um den Rand
        // zu breit: ein Video im Weg ohne Leinwand stand darin mit einem
        // knappen Punkt Schwarz oben und unten, und die Leinwand rechnete
        // ihre Punkte für eine Fläche, die zwei Punkte grösser war, als sie
        // gezeigt wurde. Und die Ebenen liegen im Inneren; darauf beziehen
        // sich ihre Anteile, also auch `--canvas-width`/`--canvas-height`. Mit
        // der äusseren Grösse klaffte bei einem Schiebe-Übergang auf halbem
        // Weg eine schwarze Fuge zwischen den beiden Bildern.
        const kastenStil = getComputedStyle(canvas);
        const randX = parseFloat(kastenStil.borderLeftWidth) + parseFloat(kastenStil.borderRightWidth) || 0;
        const randY = parseFloat(kastenStil.borderTopWidth) + parseFloat(kastenStil.borderBottomWidth) || 0;
        const scale = Math.max(0, Math.min((frei.width - randX) / width, (frei.height - randY) / height));
        // `+ 1e-6`: 720 · (224 / 1280) ist in Gleitkomma 125,99999999999999,
        // und abgerundet fehlte der Breite ein ganzer Punkt -- gemessen, nicht
        // ausgedacht (24. September, iPhone-13-Fenster).
        const innenB = Math.floor(width * scale + 1e-6);
        const innenH = Math.floor(height * scale + 1e-6);
        canvas.style.width = `${innenB + randX}px`;
        canvas.style.height = `${innenH + randY}px`;
        canvas.style.setProperty('--canvas-height', `${innenH}px`);
        // Auch die Breite: die Ebenen verschieben sich in Bruchteilen der
        // Leinwand, und seit 7.57e steht das in `transform` statt in
        // `left`/`top`. Ein Prozentwert in `translate` bezieht sich auf das
        // Element selbst -- gebraucht wird der Bezug auf die Leinwand.
        canvas.style.setProperty('--canvas-width', `${innenB}px`);
        // Und wie gross ein Punkt des Films hier ist: der Weichzeichner misst
        // in Punkten des Films (`gblur=sigma=` im Export), CSS in Punkten des
        // Bildschirms (7.173).
        canvas.style.setProperty('--film-punkt', `${(innenB / width).toFixed(6)}px`);
        // Wie viele echte Punkte die Fläche hat: die Grafikkarte malt in
        // Gerätepunkten, nicht in CSS-Punkten. Mehr als das Bild selbst hat
        // bringt nichts -- deshalb gedeckelt.
        const dichte = Math.min(window.devicePixelRatio || 1, 3);
        glMasse.current = {
          breite: Math.min(width, Math.round(innenB * dichte)),
          hoehe: Math.min(height, Math.round(innenH * dichte)),
        };
      }
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    // Auch die Leisten: wird der Transport enger oder kommt der Werkzeugkasten
    // dazu, aendert sich der Platz fuer die Leinwand, ohne dass die Buehne
    // selbst ihre Groesse aendert.
    for (const leiste of leisten()) observer.observe(leiste);
    return () => observer.disconnect();
  }, [width, height, transportLage]);

  /**
   * Was gemalt werden soll, immer auf dem neuesten Stand.
   *
   * Der Zeichenschritt läuft aus einem `requestAnimationFrame` heraus und
   * nicht aus einem Durchlauf -- er darf deshalb nichts aus dem Durchlauf
   * festhalten, sonst malt er beim Abspielen das Bild von vorhin.
   */
  const malStand = useRef({ base, overlays, assetById, videoDims, width, height, brandKit, brandLogoUrl });
  malStand.current = { base, overlays, assetById, videoDims, width, height, brandKit, brandLogoUrl };

  const male = useCallback(() => {
    const m = maler.current;
    if (!m) return;
    const { breite, hoehe } = glMasse.current;
    if (breite < 1 || hoehe < 1) return;
    m.masse(breite, hoehe);
    const stand = malStand.current;
    const leinwandSeiten = stand.width / Math.max(1, stand.height);
    const ebenen: GlEbene[] = [];
    const dichte = Math.min(window.devicePixelRatio || 1, 3);

    // Das Logo, wenn eines gebraucht wird: einmal laden, danach male() rufen.
    const logo = (): HTMLImageElement | null => {
      const url = stand.brandLogoUrl;
      if (!url) return null;
      if (logoBild.current?.url !== url) {
        const bild = new Image();
        bild.crossOrigin = 'anonymous';
        bild.onload = () => male();
        bild.src = url;
        logoBild.current = { url, bild };
      }
      const b = logoBild.current.bild;
      return b.complete && b.naturalWidth ? b : null;
    };

    /**
     * Eine gesetzte Ebene (Text oder Bauchbinde) als Bild in Leinwandgrösse.
     * Gemalt wird nur, wenn sich der Schlüssel geändert hat.
     */
    const gesetztesBild = (clip: Clip): { quelle: HTMLCanvasElement; version: number } | null => {
      let schluessel: string;
      let male2: (flaeche: HTMLCanvasElement) => void;
      if (clip.source.kind === 'text') {
        const b = { text: clip.source.text, style: clip.source.style, breite, hoehe, dichte };
        schluessel = textSchluessel(b);
        male2 = (f) => maleText(f, b);
      } else if (clip.source.kind === 'brand') {
        const template = stand.brandKit?.templates.find((t) => t.id === (clip.source as { templateId: string }).templateId);
        if (!template) return null;
        const b = { template, fields: clip.source.fields, brandKit: stand.brandKit, logo: logo(), breite, hoehe };
        schluessel = markenSchluessel(b);
        male2 = (f) => maleMarke(f, b);
      } else {
        return null;
      }
      let eintrag = gesetzt.current.get(clip.id);
      if (!eintrag) {
        eintrag = { flaeche: document.createElement('canvas'), schluessel: '', version: 0 };
        gesetzt.current.set(clip.id, eintrag);
      }
      if (eintrag.schluessel !== schluessel) {
        male2(eintrag.flaeche);
        eintrag.schluessel = schluessel;
        eintrag.version += 1;
      }
      return { quelle: eintrag.flaeche, version: eintrag.version };
    };
    for (const b of stand.base) {
      const tf = transformAt(b.clip, useEditor.getState().playhead - b.clip.start);
      const werte = farbwerteVon(b.clip.effects);
      const wirkung = lookUndVignette(b.clip);
      // **Der Übergang als Form** (7.147): hat die Art eine, malt die
      // Leinwand sie, und die Deckung ist die *ohne* den Übergang. Sonst
      // bleibt es bei der Überblendung, die in `opacity` schon steckt.
      // **Der Weg durch eine Farbe** (7.149): eine deckende Fläche darunter,
      // und die beiden Clips mit ihrem Gewicht darüber. Sie kommt *vor* den
      // abgehenden Clip, denn der liegt schon auf ihr.
      if (b.dipFuellung) {
        ebenen.push({
          quelle: null, farbe: farbeVon(b.dipFuellung), kasten: { breite: 1, hoehe: 1 },
          x: 0.5, y: 0.5, scale: 1, rotate: 0, deckung: 1,
        });
      }
      const ueb = formUebergang(b.uebergang, b.raster);
      // Malt die Leinwand die Form, deckt die Ebene voll -- die Kante macht
      // den Übergang, nicht die Durchsichtigkeit.
      const deckungB = ueb && b.uebergang ? b.uebergang.deckung : b.opacity;
      const uebB = ueb ? { uebergang: ueb } : {};
      // **Ein Schiebe-Übergang rückt die ganze Ebene** (7.148): keine Maske,
      // sondern eine andere Lage. Beide Bilder wandern, und zusammen decken
      // sie das Bild genau ab.
      const schubB = b.schub ?? 0;
      /**
       * **Was jede Ebene der Basisspur gemeinsam hat -- an *einer* Stelle.**
       *
       * Hier stand es viermal nebeneinander: für die Farbfläche, den Text,
       * das Standbild und das Video. Beim Bauen von 7.147 und 7.149 habe ich
       * drei davon nachgezogen und das Video vergessen -- ein Clip mit Video
       * auf der Basisspur bekam den Übergang gar nicht, und der Film-Vergleich
       * konnte es nicht sehen, weil er mit Farbflächen malt.
       *
       * Vier Abschriften einer Zeile sind drei Gelegenheiten, eine zu
       * vergessen. Jetzt steht sie einmal da.
       */
      const gemeinsam = { deckung: deckungB, werte, ...wirkung, ...uebB };
      const lageB = { x: tf.x + schubB, y: tf.y, scale: tf.scale, rotate: tf.rotate };
      if (b.clip.source.kind === 'brand') Object.assign(lageB, markenMitte(b.clip, stand.brandKit, lageB));
      // Eine Farbfläche auf der Basisspur füllt das Bild (7.69b).
      if (b.clip.source.kind === 'color') {
        ebenen.push({ quelle: null, farbe: farbeVon(b.clip.source.color), kasten: { breite: 1, hoehe: 1 },
          ...lageB, x: 0.5 + schubB, y: 0.5, scale: 1, rotate: 0, ...gemeinsam });
        continue;
      }
      // Text und Bauchbinde (7.69d): gesetzt in Canvas 2D, gemalt wie jede
      // andere Ebene -- damit liegt, was unten steht, auch unten.
      if (b.clip.source.kind === 'text' || b.clip.source.kind === 'brand') {
        const bild = gesetztesBild(b.clip);
        if (bild) ebenen.push({ ...bild, kasten: { breite: 1, hoehe: 1 }, ...lageB, ...gemeinsam });
        continue;
      }
      if (b.clip.source.kind !== 'asset') continue;
      // Ein Standbild auf der Basisspur: eingepasst wie ein Video.
      if (istStandbild(stand.assetById, b.clip)) {
        const bild = standbilder.current.get(b.clip.id);
        if (!bild?.complete || !bild.naturalWidth) continue;
        ebenen.push({ quelle: bild, version: 1, kasten: einpassen(bild.naturalWidth / bild.naturalHeight, leinwandSeiten),
          ...lageB, ...gemeinsam });
        continue;
      }
      const element = b.slot === 0 ? videoA.current : videoB.current;
      // Ein Element ohne Bild würde eine leere Textur hochladen und die
      // Fläche schwarz machen -- das ist schlechter als das Standbild, das
      // der Browser selbst noch zeigt.
      if (!element || element.readyState < 2 || !element.videoWidth) continue;
      ebenen.push({
        quelle: element,
        version: bildNummer(element),
        kasten: einpassen(element.videoWidth / element.videoHeight, leinwandSeiten),
        ...lageB,
        ...gemeinsam,
      });
    }
    // Die Ebenen darüber (7.69b): so breit wie die Leinwand, die Höhe folgt
    // dem Bild -- dieselbe Regel wie im Export. Text und Bauchbinde bleiben
    // Elemente und liegen über allem, was hier gemalt wird; eine Textebene
    // *unter* einer Videoebene kommt damit oben an. Das ist Stufe d.
    const kopf = useEditor.getState().playhead;
    for (const o of stand.overlays) {
      if (o.opacity <= 0) continue;
      const tf = transformAt(o.clip, kopf - o.clip.start);
      const an = slideOffset(o.clip.animation, kopf - o.clip.start);
      const lage = {
        x: tf.x + an.dx + (o.schub ?? 0), y: tf.y + an.dy, scale: tf.scale, rotate: tf.rotate,
        deckung: formUebergang(o.uebergang, o.raster) && o.uebergang ? o.uebergang.deckung : o.opacity,
        ...(formUebergang(o.uebergang, o.raster) ? { uebergang: formUebergang(o.uebergang, o.raster)! } : {}),
        werte: farbwerteVon(o.clip.effects),
        // Wie die Spur auf dem liegt, was darunter schon gemalt ist (7.132).
        // Dieselben vier, die das Stilblatt als `mix-blend-mode` setzt.
        mischart: MISCH_GL[o.mischmodus] ?? 0,
        // Die Form, in der die Ebene laeuft (7.82). Bis zum 22. September kam
        // sie hier nicht mit -- der Export schnitt sie, die Leinwand malte das
        // ganze Rechteck (7.138).
        maske: o.clip.maske && o.clip.maske.form !== 'keine' ? o.clip.maske : undefined,
        ...lookUndVignette(o.clip),
      };
      // Eine Bauchbinde dreht und skaliert um die Mitte ihres Inhalts (7.19a);
      // gemalt wird sie weiter bildfüllend, nur die Mitte des Bildes rückt.
      if (o.clip.source.kind === 'brand') {
        const m = markenMitte(o.clip, stand.brandKit, lage);
        lage.x = m.x;
        lage.y = m.y;
      }
      // Eine Farbfläche hat kein Seitenverhältnis: sie füllt den Rahmen, auf
      // der Basis wie als Ebene (7.103). Vorher stand hier 16:9 -- im
      // Hochkant-Projekt war eine Einblendung damit ein Band in der Mitte,
      // im Film aber die ganze Fläche (`color=c=…:s=WxH` im Export).
      if (o.clip.source.kind === 'color') {
        // Gedreht wird sie trotzdem: der Export dreht jede Ebene, gleich aus
        // was sie besteht (`rotate=` in `compile.ts`, hinter `scale`). Der
        // Rahmen ist ihr Kasten, der Winkel ihr Winkel.
        ebenen.push({ quelle: null, farbe: farbeVon(o.clip.source.color), kasten: { breite: 1, hoehe: 1 }, ...lage });
        continue;
      }
      if (o.clip.source.kind === 'text' || o.clip.source.kind === 'brand') {
        const bild = gesetztesBild(o.clip);
        if (bild) ebenen.push({ ...bild, kasten: { breite: 1, hoehe: 1 }, ...lage });
        continue;
      }
      if (o.clip.source.kind !== 'asset') continue;
      const video = fremdeBilder.current.get(o.clip.id);
      if (video && video.readyState >= 2 && video.videoWidth) {
        ebenen.push({ quelle: video, version: bildNummer(video), kasten: ebenenKasten(video.videoWidth / video.videoHeight, leinwandSeiten), ...lage });
        continue;
      }
      // Kein Video oder noch keines: das Standbild, wie im Element auch.
      const bild = standbilder.current.get(o.clip.id);
      if (!bild?.complete || !bild.naturalWidth) continue;
      ebenen.push({ quelle: bild, version: 1, kasten: ebenenKasten(bild.naturalWidth / bild.naturalHeight, leinwandSeiten), ...lage });
    }
    // Was nicht mehr im Bild ist, braucht auch keine Zeichenfläche mehr.
    const dabei = new Set([...stand.base.map((b) => b.clip.id), ...stand.overlays.map((o) => o.clip.id)]);
    for (const id of gesetzt.current.keys()) if (!dabei.has(id)) gesetzt.current.delete(id);
    m.zeichne(ebenen, stand.width);
  }, []);

  /**
   * Ein Instrument, wie `window.__ton` und `window.__bilder`: was an einer
   * Stelle der Leinwand steht. Der Durchgang liest damit nach, dass die
   * Grafikkarte wirklich malt -- „das Bild ist da“ ist sonst eine Behauptung.
   */
  useEffect(() => {
    if (!GL_GEWUENSCHT) return;
    window.__leinwand = (x = 0.5, y = 0.5) => {
      male();
      return JSON.stringify(maler.current?.punkt(x, y) ?? null);
    };
    // Und das ganze Bild (7.136), fuer den Vergleich mit dem Film.
    window.__leinwandBild = () => {
      male();
      return maler.current?.bild() ?? '';
    };
    // Die Zahlen, die entscheiden, warum ein Video ruckelt (7.69h).
    window.__leinwandBericht = () => {
      const b = leinwandBericht();
      const s = Math.max(0.001, (performance.now() - takte.seit) / 1000);
      return JSON.stringify({ ...b, bildtaktJeSekunde: Math.round((takte.n / s) * 10) / 10 });
    };
    window.__leinwandZurueck = () => {
      messungZurueck();
      takte.n = 0;
      takte.seit = performance.now();
    };
    return () => {
      delete window.__leinwand;
      delete window.__leinwandBild;
      delete window.__leinwandBericht;
      delete window.__leinwandZurueck;
    };
  }, [male]);

  // Die Zahlen vom Gerät gehen beim Abspielen von selbst an den Server
  // (7.69j) -- alle zehn Sekunden und beim Anhalten.
  const projektId = useEditor((s) => s.project?.id ?? null);
  /**
   * Die Garantie fürs vollständige Abspielen (7.69o).
   *
   * Sascha, 9. September: „wir müssen das vollständige Abspielen der Timeline
   * in iOS garantieren!“ Fehlen einem Video die Daten, hält die Wiedergabe an
   * und läuft von selbst weiter, sobald sie da sind -- statt weiterzulaufen
   * und Sekunden nie zu zeigen. Gewartet wird nur, wenn Warten hilft: liegt
   * die Stelle geladen vor und kommt trotzdem kein Bild, ist es der Dekoder
   * (siehe pufferBedarf).
   */
  const puffernAn = useRef<{ seit: number } | null>(null);
  const [puffert, setPuffert] = useState(false);
  /**
   * Für die Stellen, die nicht neu rechnen sollen, wenn sich das ändert:
   * das Vorladen fragt es nur nach, es hängt nicht davon ab (7.69r).
   */
  const puffertRef = useRef(false);
  puffertRef.current = puffert;
  useEffect(() => {
    if (!playing && !puffernAn.current) return;
    const bereiche = (el: HTMLVideoElement): [number, number][] => {
      const raus: [number, number][] = [];
      for (let i = 0; i < el.buffered.length; i++) raus.push([el.buffered.start(i), el.buffered.end(i)]);
      return raus;
    };
    const zuPruefen = (): { el: HTMLVideoElement; clip: Clip }[] => {
      const kandidaten: { el: HTMLVideoElement; clip: Clip }[] = [];
      for (const slot of [0, 1] as const) {
        const eintrag = baseRef.current.find((b) => b.slot === slot);
        const el = slot === 0 ? videoA.current : videoB.current;
        if (el && eintrag && eintrag.clip.source.kind === 'asset' && eintrag.opacity > 0.5) kandidaten.push({ el, clip: eintrag.clip });
      }
      for (const o of overlaysRef.current) {
        const el = fremdeBilder.current.get(o.clip.id);
        if (el && o.clip.source.kind === 'asset' && o.opacity > 0.5) kandidaten.push({ el, clip: o.clip });
      }
      // Und der Film, wenn er läuft: er ist das einzige Bild (7.71).
      // Nur das Element, das gerade spielt: das andere wartet auf sein Stück,
      // und solange die Ebenen spielen, wartet niemand auf den Film (7.239).
      const fel = filmAktiv.current;
      if (fel && fel.dataset.src) kandidaten.push({ el: fel, clip: filmClipRef.current });
      return kandidaten;
    };
    const standVon = (el: HTMLVideoElement) => ({ fehler: Boolean(el.error), bereit: el.readyState, geladen: bereiche(el) });
    /** Wie viel von diesem Clip überhaupt noch kommt, in Sekunden der Datei. */
    const restVon = (clip: Clip): number => {
      const kopf = useEditor.getState().playhead;
      // Mit einer Rampe (S6) ist der Rest die Fläche unter dem verbleibenden
      // Stück der Tempokurve, nicht Restzeit mal Anfangstempo -- sonst hielte
      // sich die Wiedergabe für satt, während der schnelle Teil noch kommt.
      const r = rampeVon(clip);
      const tau = Math.min(clip.duration, Math.max(0, kopf - clip.start));
      return Math.max(0, quellZeit(clip.duration, clip.duration, r) - quellZeit(tau, clip.duration, r));
    };
    /**
     * Wie ein Element gerade steht, als eine Zeile für den Verlauf des
     * Berichts: welcher Clip, `readyState`, die gesuchte Stelle und wie weit
     * das Geladene darüber hinausreicht (negativ: die Stelle liegt nicht im
     * Geladenen).
     */
    const beschreibe = (el: HTMLVideoElement, clip: Clip): string => {
      const soll = medienzeitJetzt(clip);
      const drin = bereiche(el).find(([von, bis]) => soll >= von - 0.05 && soll <= bis);
      const vorsprung = drin ? drin[1] - soll : -1;
      const wer = el === filmRef.current ? 'film' : el === videoA.current ? 'A' : el === videoB.current ? 'B' : 'ebene';
      return `${wer}:${clip.id} bereit=${el.readyState} soll=${soll.toFixed(2)} vorsprung=${vorsprung.toFixed(2)}`;
    };
    /** Das erste Element, dem Daten fehlen, beschrieben -- oder `null`. */
    const fehltDaten = (): string | null => {
      for (const { el, clip } of zuPruefen()) if (pufferBedarf(standVon(el), medienzeitJetzt(clip))) return beschreibe(el, clip);
      return null;
    };
    /**
     * Und die andere Schwelle (7.69r): weitergelaufen wird erst, wenn *alle*
     * wieder Vorrat haben -- nicht schon, wenn gerade eben nichts mehr fehlt.
     * Sonst hält es nach drei Zehntelsekunden wieder an, und das war Saschas
     * „drückt ganz oft Pause und Play“.
     */
    const alleSatt = (gewartetMs: number): boolean => {
      // Wie viel Vorrat verlangt wird, sinkt mit der Wartezeit (7.69t):
      // sonst wartet eine langsame Leitung bis zur Acht-Sekunden-Grenze und
      // ruckt danach ohnehin los -- genau das stand im Befund vom iPhone.
      const noetig = noetigerVorrat(gewartetMs);
      return zuPruefen().every(({ el, clip }) => pufferSatt(standVon(el), medienzeitJetzt(clip), restVon(clip), noetig));
    };
    const pruefe = () => {
      const halt = puffernAn.current;
      if (halt) {
        // Hat jemand selbst wieder auf Abspielen getippt, ist das Warten vorbei.
        if (playing) {
          puffernAn.current = null;
          pufferEndet(performance.now() - halt.seit, false);
          setPuffert(false);
          return;
        }
        const dauer = performance.now() - halt.seit;
        const abgelaufen = pufferAbgelaufen(dauer);
        // Ein Boden von einer Viertelsekunde: was innerhalb eines Taktes
        // anhält und wieder anfängt, sieht aus wie ein Fehler, auch wenn es
        // technisch stimmt.
        if ((alleSatt(dauer) && dauer >= 250) || abgelaufen) {
          puffernAn.current = null;
          pufferEndet(dauer, abgelaufen, zuPruefen().map(({ el, clip }) => beschreibe(el, clip)).join(' | '));
          setPuffert(false);
          setPlaying(true);
        }
        return;
      }
      const fehlt = playing && !pufferGenug() ? fehltDaten() : null;
      if (fehlt) {
        puffernAn.current = { seit: performance.now() };
        pufferBeginnt(fehlt);
        // Der Messung sagen, dass das kein Anhalten ist, sondern ein Warten
        // (7.116) -- sonst wirft sie das angefangene Fenster weg.
        messungWartet();
        setPuffert(true);
        setPlaying(false);
      }
    };
    const zeitgeber = window.setInterval(pruefe, 200);
    return () => window.clearInterval(zeitgeber);
  }, [playing, setPlaying]);

  useEffect(() => {
    if (!playing) return;
    messungStartet(projektId);
    return () => messungEndet(projektId);
  }, [playing, projektId]);

  // Der Bildtakt der beiden Basisflächen: die Leinwand lädt ein Bild nur
  // hoch, wenn es ein neues ist (7.69f).
  useEffect(() => {
    if (!GL_GEWUENSCHT) return;
    const stopp = [videoA.current, videoB.current].filter((v): v is HTMLVideoElement => !!v).map(bildtakt);
    return () => stopp.forEach((s) => s());
  }, []);

  useEffect(() => {
    if (!GL_GEWUENSCHT) return;
    const flaeche = glLeinwand.current;
    if (!flaeche) return;
    maler.current = Maler.an(flaeche);
    // Kein WebGL2: dann bleibt es beim alten Weg. Eine Vorschau, die gar
    // nichts zeigt, wäre schlimmer als eine, die ruckelt.
    if (!maler.current) flaeche.dataset.gl = 'nein';
    else flaeche.dataset.gl = 'ja';
    male();
    return () => {
      maler.current?.frei();
      maler.current = null;
    };
  }, [male]);

  // Beim Stehen genügt ein Bild je Durchlauf; beim Abspielen malt sie mit.
  useEffect(() => {
    if (GL_GEWUENSCHT) male();
  });
  // **Und wenn ein Video im Stehen fertig gesucht hat** (7.162). Ein Element,
  // das noch sucht, lässt `male` aus (sonst käme eine leere Textur); kam das
  // Bild erst *nach* dem letzten Durchlauf, malte niemand nach, und die
  // Leinwand zeigte an seiner Stelle Schwarz, bis sich irgendetwas anderes
  // rührte. Gefunden am 24. September im Wege-Vergleich: zwei volle Läufe,
  // zweimal fehlte der gehende Clip, jedes Mal in einer anderen Szene.
  // Medienereignisse steigen nicht auf, laufen aber beim Einfangen durch die
  // Vorfahren -- ein Horcher am Kasten reicht für alle Videos darin.
  useEffect(() => {
    const kasten = stage.current;
    if (!GL_GEWUENSCHT || !kasten) return;
    const nachmalen = (): void => male();
    kasten.addEventListener('seeked', nachmalen, true);
    kasten.addEventListener('loadeddata', nachmalen, true);
    return () => {
      kasten.removeEventListener('seeked', nachmalen, true);
      kasten.removeEventListener('loadeddata', nachmalen, true);
    };
  }, [male]);
  useEffect(() => {
    if (!GL_GEWUENSCHT || !playing) return;
    let raf = 0;
    const takt = (): void => {
      male();
      raf = requestAnimationFrame(takt);
    };
    raf = requestAnimationFrame(takt);
    return () => cancelAnimationFrame(raf);
  }, [playing, male]);

  // Wer gerade den Takt angibt: das Element der Basisspur, das man sieht.
  const taktgeber = useRef<{ element: HTMLVideoElement; clip: Clip } | null>(null);
  // Ob seine Uhr überhaupt läuft. Ein Video, das auf `play()` steht, aber
  // gerade nachlädt, hat eine Uhr, die stillsteht -- folgt der Kopf ihr blind,
  // steht die ganze Wiedergabe, und man sieht nicht warum.
  const taktStand = useRef<TaktStand>({ zuletzt: null, seit: 0 });
  /** Seit wann der Kopf auf ein Element wartet, das play() hat, aber noch kein Bild. */
  const warten = useRef(0);
  /** Ob dieses Warten schon als „abgelaufen“ gezählt ist -- sonst zählte jedes Bild neu. */
  const wartenGezaehlt = useRef(false);
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;
  const baseRef = useRef(base);
  baseRef.current = base;
  const filmClipRef = useRef(filmClip);
  filmClipRef.current = filmClip;
  useEffect(() => {
    setzeTaktgeber(() => {
      zuletztGetaktetUm = performance.now();
      let m = taktgeber.current;
      // Ohne Video auf der Basisspur gibt die vorderste Videoebene den Takt
      // (7.69l). Saschas Projekte haben die Videos auf den Ebenen, die
      // Basisspur ist leer -- und ein Video, das der Wanduhr folgen muss,
      // kann ihr nur hinterherspringen.
      let ebene: string | null = null;
      if (!m) {
        for (const o of overlaysRef.current) {
          if (o.clip.source.kind !== 'asset') continue;
          const element = fremdeBilder.current.get(o.clip.id);
          if (!element) continue;
          m = { element, clip: o.clip };
          ebene = o.clip.id;
          break;
        }
      }
      taktendeEbene = ebene;
      const wanduhr = (): null => {
        taktStand.current = { zuletzt: null, seit: 0 };
        zuletztGetaktet = 'wanduhr';
        return null;
      };
      /** Ein Warten auf das erste Bild ist vorbei -- fertig oder abgelaufen (7.69q). */
      const wartenEnde = (abgelaufen: boolean): void => {
        if (!warten.current) return;
        if (!wartenGezaehlt.current) bildWartenEndet(performance.now() - warten.current, abgelaufen);
        warten.current = 0;
        wartenGezaehlt.current = false;
      };
      if (!m || m.element.paused) {
        wartenEnde(false);
        return wanduhr();
      }
      const t = zeitleistenZeit(m.clip, m.element.currentTime);
      const imClip = t >= m.clip.start && t <= m.clip.start + m.clip.duration;
      if (m.element.readyState < 2) {
        // Es hat play(), aber noch kein Bild. Läuft der Kopf jetzt davon,
        // startet das Video hinten und muss springen -- also wartet der Kopf
        // kurz an seiner Stelle. Nicht ewig: ein Element, das nie dekodiert,
        // dürfte sonst die ganze Wiedergabe festhalten.
        taktStand.current = { zuletzt: null, seit: 0 };
        // Steht der Kopf gar nicht in diesem Clip, ist das kein Warten,
        // sondern ein Element, das nichts zu zeigen hat -- dann taktet die
        // Wanduhr, und gezählt wird nichts. Sonst stand in jedem Befund eine
        // Handvoll Haltungen von null Millisekunden (gefunden bei der
        // Durchsicht am 11. September).
        if (!imClip) {
          wartenEnde(false);
          zuletztGetaktet = 'wanduhr';
          return null;
        }
        if (!warten.current) {
          warten.current = performance.now();
          wartenGezaehlt.current = false;
          bildWartenBeginnt();
        }
        const gewartet = performance.now() - warten.current;
        if (gewartet < 1500) {
          zuletztGetaktet = `wartet:${m.clip.id}`;
          return t;
        }
        // Abgelaufen: einmal zählen und dann weiterlaufen, nicht je Bild.
        if (!wartenGezaehlt.current) {
          bildWartenEndet(gewartet, true);
          wartenGezaehlt.current = true;
        }
        zuletztGetaktet = 'wanduhr';
        return null;
      }
      wartenEnde(false);
      const { laeuft, stand } = taktLaeuft(taktStand.current, m.element.currentTime, performance.now());
      taktStand.current = stand;
      if (!laeuft) {
        zuletztGetaktet = 'wanduhr';
        return null;
      }
      // Über den Clip hinaus taktet er nicht mehr: den Übergang zum nächsten
      // zählt wieder die Wanduhr, sonst bliebe der Kopf am Ende hängen.
      if (!imClip) return wanduhr();
      zuletztGetaktet = m.clip.id;
      return t;
    });
    // Nur, was gerade gilt: eine Meldung, die seit einer halben Sekunde niemand
    // erneuert hat, ist keine mehr (7.171).
    window.__taktgeber = () => (performance.now() - zuletztGetaktetUm < 500 ? zuletztGetaktet : 'nichts');
    return () => {
      setzeTaktgeber(null);
      delete window.__taktgeber;
    };
  }, []);

  // Keep the base-track elements pointed at the right file and moment.
  useEffect(() => {
    // Im Filmmodus rührt hier nichts: die Flächen stehen nicht einmal im
    // Dokument, und ihre Dekoder gehören dem Film (7.71).
    if (film) return;
    miss('elemente', () => {
    // Das sichtbarste Element der Basisspur gibt den Takt an; bei einem
    // Übergang ist das das, was gerade obenauf liegt.
    const fuehrend = base
      .filter((b) => b.clip.source.kind === 'asset')
      .reduce<typeof base[number] | undefined>((beste, b) => (!beste || b.opacity > beste.opacity ? b : beste), undefined);
    let gefunden = false;

    for (const slot of [0, 1] as const) {
      const element = slot === 0 ? videoA.current : videoB.current;
      if (!element) continue;
      const entry = base.find((b) => b.slot === slot);
      if (!entry || entry.clip.source.kind !== 'asset') {
        halte(element);
        // Die leere Fläche steht nicht herum: liegt hier der nächste Clip,
        // holt sie sich seine Datei jetzt und sucht schon einmal seinen
        // Anfang. Wenn er dran ist, ist er da.
        if (naechsteBasis && naechsteBasis.slot === slot) {
          const kommt = assetById.get(naechsteBasis.assetId)?.proxyUrl;
          // Aber nicht, während der laufende Clip selbst auf Daten wartet
          // (7.69r): eine zweite Datei anzufordern, während die erste nicht
          // durchkommt, nimmt genau der die Leitung weg. Was schon angefangen
          // hat, behält sie -- abbrechen und neu anfangen wäre schlimmer.
          if (kommt && element.dataset.src !== kommt && !puffertRef.current) {
            element.dataset.src = kommt;
            element.dataset.want = String(naechsteBasis.sourceIn);
            // Aus dem Arbeitsspeicher, wenn die Datei dort liegt (7.69s):
            // dann holt das Element beim Abspielen nichts mehr nach.
            element.src = ausDemRam(kommt) ?? kommt;
            // Kommt sie erst danach an, schwenkt die *wartende* Fläche noch
            // um: sie steht still, da kostet ein Wechsel nichts. Beim
            // laufenden Bild wäre er ein Ruckler und unterbleibt.
            void inDenRam(kommt).then((blob) => {
              if (!blob || element.dataset.src !== kommt || !element.paused) return;
              if (element.src === blob) return;
              element.src = blob;
            });
          }
        } else if (element.dataset.src) {
          // Und wenn auch nichts kommt, gibt sie die Datei wieder her (7.68).
          //
          // Anhalten reicht nicht: ein `<video>` auf Pause behaelt seinen
          // Dekoder, und davon hat ein iPhone nicht viele. Zwei Basisflaechen,
          // eine je sichtbarer Bildebene, eine zum Vorladen -- das sind vier,
          // von denen hoechstens zwei etwas zeigen. Sascha, 8. September:
          // „oder ein anderes Video drin, stottert es wieder total.“
          //
          // `removeAttribute` und `load()` sind der Weg, den die Browser dafuer
          // vorsehen; `src = ''` laedt die Seite selbst noch einmal.
          delete element.dataset.src;
          delete element.dataset.want;
          element.removeAttribute('src');
          element.load();
        }
        continue;
      }
      const asset = assetById.get(entry.clip.source.assetId);
      const src = asset?.proxyUrl;
      if (!src) continue;
      if (element.dataset.src !== src) {
        element.dataset.src = src;
        element.src = ausDemRam(src) ?? src;
      }
      void inDenRam(src);
      // Wohin das Element soll, sobald es kann. Ein `currentTime`, das
      // gesetzt wird, bevor die Datei ihre Kopfdaten hat, verfällt still --
      // deshalb steht der Wunsch am Element, und `onLoadedMetadata` holt ihn
      // nach. Sonst bleibt die Vorschau nach dem Öffnen eines Projekts
      // schwarz: gesucht wurde, bevor es etwas zu suchen gab.
      element.dataset.want = String(entry.mediaTime);
      const speed = Math.min(4, Math.max(0.25, tempoJetzt(entry.clip)));
      const taktend = entry === fuehrend;
      if (taktend) {
        taktgeber.current = { element, clip: entry.clip };
        gefunden = true;
      }

      if (playing && taktend) {
        // Der Taktgeber wird nicht korrigiert: er *ist* die Zeit. Jeder
        // Sprung hier setzt auf iOS die Dekodierung neu auf.
        setzeTempo(element, speed);
      } else if (playing) {
        // Alle anderen holen den Abstand über das Tempo auf, nicht per Sprung
        // -- gegen die genaue Zeit (7.69i), nicht gegen die zusammengefasste.
        // Solange es sucht, ist sein currentTime das Ziel, nicht der Stand:
        // daran gibt es nichts zu messen (7.69l).
        if (!element.seeking) {
          const jetzt = medienzeitJetzt(entry.clip);
          const { rate, springen } = angleichenRuhig(jetzt, element.currentTime, speed, element.playbackRate);
          if (Math.abs(element.playbackRate - rate) > 0.004) zaehleBild('tempo-basis');
          setzeTempo(element, rate);
          // Ein Sprung setzt auf dem Telefon die Dekodierung neu auf. Wie oft
          // das wirklich vorkommt, sagt die Zahl und nicht das Bauchgefühl.
          if (springen) springe(element, jetzt, speed, 'sprung-basis');
        }
      } else {
        setzeTempo(element, speed);
        // Beim Stehen und Suchen gibt der Kopf vor, wo das Bild steht.
        stelleImStehen(element, entry.mediaTime);
      }

      const audio = audible.find((a) => a.clip.id === entry.clip.id);
      // Ein Element kann nicht lauter als 1 (und wirft bei mehr); der Pegel
      // darüber geht nur durch die Tonkette.
      element.volume = audio && !monitorMuted ? Math.min(1, audio.volume) : 0;
      element.muted = !audio;
      if (playing) starte(element);
      if (!playing) halte(element);
    }
    if (!gefunden) taktgeber.current = null;
    });
  }, [base, assetById, playing, audible, naechsteBasis, film]);

  /**
   * Der Film läuft (7.71): eine Datei, ein Dekoder. Er gibt auch den Takt --
   * der Kopf folgt ihm, statt ihm davonzulaufen. Ohne Abhängigkeiten, wie bei
   * den Ebenen: der Abgleich gehört zu jedem Bild.
   */
  useEffect(() => {
    const element = filmRef.current;
    if (!element || !film?.url) return;
    if (element.dataset.src !== film.url) {
      element.dataset.src = film.url;
      element.src = ausDemRam(film.url) ?? film.url;
    }
    void inDenRam(film.url);
    element.muted = monitorMuted;
    element.volume = 1;
    taktgeber.current = { element, clip: filmClip };
    // Ein Stück fängt bei sich selbst an: der Kopf steht auf der Zeitleiste,
    // die Datei zählt ab null (7.71b).
    const soll = useEditor.getState().playhead - film.von;
    element.dataset.want = String(soll);
    if (playing) {
      if (!element.seeking) {
        const { rate, springen } = angleichenRuhig(soll, element.currentTime, 1, element.playbackRate);
        if (Math.abs(element.playbackRate - rate) > 0.004) zaehleBild('tempo-film');
        setzeTempo(element, rate);
        if (springen) springe(element, soll, 1, 'sprung-film');
      }
      starte(element);
    } else {
      setzeTempo(element, 1);
      stelleImStehen(element, soll);
      halte(element);
    }
    // Das nächste Stück liegt schon im anderen Element und wartet am Anfang:
    // ohne das kostet jeder Abschnittswechsel einen Ladevorgang, und genau
    // dafür sind die Abschnitte nicht gedacht.
    const naechstes = stuecke[stueckNr + 1] ?? null;
    const vor = filmVorRef.current;
    if (vor) {
      if (naechstes && naechstes.url !== vor.dataset.src) {
        vor.dataset.src = naechstes.url;
        vor.src = ausDemRam(naechstes.url) ?? naechstes.url;
        vor.currentTime = 0;
      }
      if (naechstes) {
        const url = naechstes.url;
        void inDenRam(url).then((blob) => {
          if (blob && vor.dataset.src === url && vor.paused && vor.src !== blob) vor.src = blob;
        });
      }
      if (!naechstes && vor.dataset.src) {
        vor.removeAttribute('src');
        delete vor.dataset.src;
        vor.load();
      }
      // Stumm und still, bis es an der Reihe ist -- sonst klingt eine Stelle
      // zweimal.
      vor.muted = true;
      if (!vor.paused) halte(vor);
    }
  });

  /**
   * Solange die Ebenen spielen oder der Kopf steht: das Stück unter dem Kopf
   * oder das nächste, und das danach, liegen schon in ihren Elementen
   * (7.239). So hat das Tippen auf Abspielen sie mit freigeschaltet, und am
   * Ende einer Ebene muss nichts erst geladen werden.
   */
  useEffect(() => {
    if (film) return;
    const kopf = useEditor.getState().playhead;
    let nr = stueckBei(stuecke, kopf);
    if (nr < 0) nr = stuecke.findIndex((s) => s.von > kopf);
    if (nr < 0) {
      // Kommt kein Stück mehr, geben beide ihre Datei her: ein Element auf
      // Pause behält sonst seinen Dekoder (7.68).
      for (const el of [filmA.current, filmB.current]) {
        if (!el?.dataset.src) continue;
        halte(el);
        el.removeAttribute('src');
        delete el.dataset.src;
        el.load();
      }
      return;
    }
    for (const i of [nr, nr + 1]) {
      const stueck = stuecke[i];
      const el = (i % 2 === 1 ? filmB : filmA).current;
      if (!stueck || !el) continue;
      if (el.dataset.src !== stueck.url) {
        el.dataset.src = stueck.url;
        el.src = ausDemRam(stueck.url) ?? stueck.url;
      }
      el.muted = true;
      if (!el.paused) halte(el);
      // Steht der Kopf schon im Stück, wartet es dort -- beim Abspielen der
      // Ebenen ein Stück voraus, damit es nicht jedem Bild nachspringt.
      const soll = Math.max(0, kopf - stueck.von);
      const abstand = Math.abs(el.currentTime - soll);
      if (soll > 0 && !el.seeking && el.readyState >= 1 && abstand > (playing ? 2 : 0.25)) el.currentTime = playing ? soll + 1 : soll;
    }
  });

  /**
   * Alle Clips auf Tonspuren, die eine Datei haben -- ob sie gerade klingen
   * oder nicht. Ihre Elemente entstehen im Voraus und nicht erst, wenn der
   * Kopf sie erreicht: auf dem iPhone darf ein Tonelement nur klingen, wenn
   * sein erstes `play()` aus einer Berührung kam, und mitten in der
   * Wiedergabe ist keine Berührung mehr (7.37, siehe ton.ts).
   */
  const tonClips = useMemo(() => {
    const raus: { id: string; assetId: string; src: string }[] = [];
    for (const track of timeline.tracks) {
      if (track.kind !== 'audio') continue;
      for (const clip of track.clips) {
        if (clip.source.kind !== 'asset') continue;
        const src = assetById.get(clip.source.assetId)?.proxyUrl;
        if (src) raus.push({ id: clip.id, assetId: clip.source.assetId, src });
      }
    }
    return raus;
  }, [timeline.tracks, assetById]);

  // Die Tonkette bekommt jede Datei im Voraus zum Dekodieren. Was sie schafft,
  // läuft später durch sie; für den Rest bleibt das Element (7.39).
  useEffect(() => {
    for (const { assetId, src } of tonClips) tonkette.lege(assetId, src);
  }, [tonClips]);
  useEffect(() => () => tonkette.stoppeAlles(), []);
  useEffect(() => tonkette.lautsprecher(!monitorMuted), [monitorMuted]);
  // Beim Verlassen des Schnittplatzes den Arbeitsspeicher wieder hergeben
  // (7.69s): ein Projekt, das man geschlossen hat, darf keine hundert
  // Megabyte weiter belegen.
  useEffect(() => () => ramLeeren(), []);

  useEffect(() => {
    const pool = audioPool.current;
    const halter = tonHalter.current;
    const sollen = new Set(tonClips.map((t) => t.id));
    for (const [id, element] of pool) {
      if (sollen.has(id)) continue;
      halte(element);
      element.remove();
      pool.delete(id);
      freigeschaltet.current.delete(id);
      tonkette.vergiss(id);
    }
    for (const { id, src } of tonClips) {
      const vorhanden = pool.get(id);
      if (vorhanden) {
        if (vorhanden.dataset.quelle !== src) {
          vorhanden.dataset.quelle = src;
          vorhanden.src = src;
        }
        continue;
      }
      const element = new Audio(src);
      element.preload = 'auto';
      element.dataset.ton = id;
      element.dataset.quelle = src;
      pool.set(id, element);
      // Im Dokument und nicht daneben: manche iOS-Fassungen spielen ein
      // Element, das nirgends hängt, nur widerwillig ab.
      halter?.appendChild(element);
    }
  }, [tonClips]);

  /**
   * Der Freischalter, den die Transportleiste aus der Berührung heraus ruft:
   * jedes Tonelement bekommt sein erstes `play()`, und was jetzt nicht dran
   * ist, hält gleich wieder an. Danach ist es für den Rest der Sitzung frei.
   */
  useEffect(
    () =>
      meldeFreischalter(() => {
        // Erst das Bild: für die beiden Flächen der Basisspur gilt auf iOS
        // dieselbe Regel wie für den Ton (7.37a). Sie stehen im Dokument,
        // seit die Seite steht -- ihnen fehlte nur der Anlauf aus der
        // Berührung. Was gerade nichts zeigt, hält sofort wieder an.
        for (const slot of [0, 1] as const) {
          const element = slot === 0 ? videoA.current : videoB.current;
          const quelle = element?.dataset.src;
          if (!element || !quelle) {
            vermerkeFreischalten(`bild-${slot}:${element ? 'ohne Datei' : 'ohne Element'}`);
            continue;
          }
          // Der Schlüssel trägt die Datei mit. Wechselt das Element seine
          // Quelle, ist es auf iOS wieder verriegelt -- und ein Schlüssel,
          // der nur die Fläche nennt, hätte es für den Rest der Sitzung als
          // erledigt geführt und nie wieder angestoßen. Genau das war der
          // Fall beim zweiten Clip auf der Basisspur.
          const kennung = `bild-${slot}:${quelle}`;
          if (freigeschaltet.current.has(kennung)) {
            vermerkeFreischalten(`bild-${slot}:schon frei`);
            continue;
          }
          vermerkeFreischalten(`bild-${slot}:angestossen`);
          freigeschaltet.current.add(kennung);
          // Der Anlauf aus der Berührung muss derselbe sein, der gleich auch
          // laufen soll: stumm anstoßen und danach den Ton dazuschalten
          // heißt auf iOS, die Erlaubnis nie geholt zu haben.
          element.muted = !bildTon.current.has(slot);
          element.dataset.freiStumm = String(element.muted);
          element.dataset.frei = 'versucht';
          starte(
            element,
            (fehler) => {
            // Ein Abbruch ist keine Absage: das Element *lief*, wir haben es
            // selbst angehalten. Freigeschaltet ist es damit trotzdem.
            if (fehler && fehler.name !== 'AbortError') {
              freigeschaltet.current.delete(kennung);
              element.dataset.frei = 'abgelehnt';
              element.dataset.grund = fehler.name;
              // Eine schwarze Fläche ohne Erklärung sieht aus wie ein
              // kaputtes Programm. Sie ist es nicht -- das Gerät hat nein
              // gesagt, und beim nächsten Tippen darf es wieder.
              if (fehler.name === 'NotAllowedError') {
                setMediaError('Das Gerät hat die Wiedergabe abgelehnt. Noch einmal auf Abspielen tippen.');
              }
              return;
            }
              if (!fehler && !bildAktiv.current.has(slot)) halte(element);
              element.dataset.frei = 'ja';
            },
            true,
          );
        }
        // Der Film, wenn er läuft: er trägt den Ton, also braucht er die
        // Erlaubnis genauso wie ein Tonelement (7.71). Beide Elemente -- auch
        // das, in dem der nächste Abschnitt wartet: mitten in der Wiedergabe
        // gibt es keine Berührung mehr, mit der es sich freischalten liesse
        // (7.71b).
        for (const halter of [filmA, filmB]) {
          const element = halter.current;
          const quelle = element?.dataset.src;
          if (element && quelle) {
            const kennung = `film:${quelle}`;
            if (!freigeschaltet.current.has(kennung)) {
              freigeschaltet.current.add(kennung);
              element.muted = useEditor.getState().monitorMuted;
              element.dataset.freiStumm = String(element.muted);
              element.dataset.frei = 'versucht';
              starte(
                element,
                (fehler) => {
                  if (fehler && fehler.name !== 'AbortError') {
                    freigeschaltet.current.delete(kennung);
                    element.dataset.frei = 'abgelehnt';
                    element.dataset.grund = fehler.name;
                    if (fehler.name === 'NotAllowedError') {
                      setMediaError('Das Gerät hat die Wiedergabe abgelehnt. Noch einmal auf Abspielen tippen.');
                    }
                    return;
                  }
                  // Wer noch auf sein Stück wartet, hält gleich wieder an --
                  // sonst klänge es, bis die Ebenen übergeben (7.239).
                  if (!fehler && (!useEditor.getState().playing || element.dataset.filmWartet === 'ja')) halte(element);
                  element.dataset.frei = 'ja';
                },
                true,
              );
            }
          }
        }
        // Die Ebenen darüber: dieselbe Regel, derselbe Augenblick.
        for (const [clipId, element] of fremdeBilder.current) {
          const quelle = element.dataset.src;
          if (!quelle) {
            vermerkeFreischalten(`ebene-${clipId}:ohne Datei`);
            continue;
          }
          const kennung = `ebene-${clipId}:${quelle}`;
          if (freigeschaltet.current.has(kennung)) {
            vermerkeFreischalten(`ebene-${clipId}:schon frei`);
            continue;
          }
          vermerkeFreischalten(`ebene-${clipId}:angestossen`);
          freigeschaltet.current.add(kennung);
          element.dataset.freiStumm = String(element.muted);
          element.dataset.frei = 'versucht';
          freiVermerk.current.set(kennung, { frei: 'versucht', freiStumm: element.dataset.freiStumm });
          starte(
            element,
            (fehler) => {
              if (fehler && fehler.name !== 'AbortError') {
                freigeschaltet.current.delete(kennung);
                freiVermerk.current.delete(kennung);
                element.dataset.frei = 'abgelehnt';
                element.dataset.grund = fehler.name;
                if (fehler.name === 'NotAllowedError') {
                  setMediaError('Das Gerät hat die Wiedergabe der Ebene abgelehnt. Noch einmal auf Abspielen tippen.');
                }
                return;
              }
              element.dataset.frei = 'ja';
              const vermerk = freiVermerk.current.get(kennung);
              if (vermerk) vermerk.frei = 'ja';
            },
            true,
          );
        }
        // Und die, die gleich kommen (7.69m): jetzt anstossen, mit dem Ton,
        // den sie später haben werden -- ein stummer Anlauf holt auf iOS
        // keine Erlaubnis für Ton (7.37d). Danach gleich wieder still.
        for (const [clipId, element] of vorabBilder.current) {
          const quelle = element.dataset.src;
          if (!quelle) {
            vermerkeFreischalten(`vorab-${clipId}:ohne Datei`);
            continue;
          }
          const kennung = `vorab-${clipId}:${quelle}`;
          if (freigeschaltet.current.has(kennung)) {
            vermerkeFreischalten(`vorab-${clipId}:schon frei`);
            continue;
          }
          vermerkeFreischalten(`vorab-${clipId}:angestossen`);
          freigeschaltet.current.add(kennung);
          element.muted = element.dataset.ton !== '1';
          element.dataset.freiStumm = String(element.muted);
          element.dataset.frei = 'versucht';
          freiVermerk.current.set(kennung, { frei: 'versucht', freiStumm: element.dataset.freiStumm });
          starte(
            element,
            (fehler) => {
              if (fehler && fehler.name !== 'AbortError') {
                freigeschaltet.current.delete(kennung);
                freiVermerk.current.delete(kennung);
                element.dataset.frei = 'abgelehnt';
                element.dataset.grund = fehler.name;
                element.muted = true;
                return;
              }
              halte(element);
              element.muted = true;
              element.dataset.frei = 'ja';
              const vermerk = freiVermerk.current.get(kennung);
              if (vermerk) vermerk.frei = 'ja';
            },
            true,
          );
        }
        for (const [id, element] of audioPool.current) {
          if (freigeschaltet.current.has(id)) continue;
          freigeschaltet.current.add(id);
          // „versucht“ steht sofort da, noch in der Berührung -- daran hängt
          // die Prüfung. Ob es danach auch klingt, entscheidet der Browser.
          element.dataset.frei = 'versucht';
          starte(
            element,
            (fehler) => {
            // Ein Abbruch ist keine Absage: das Element *lief*, und wir haben
            // es selbst angehalten. Alles andere heißt: nicht durchgekommen,
            // beim nächsten Tippen noch einmal. Der Grund bleibt am Element
            // stehen; auf einem fremden Gerät ist das der einzige Weg
            // herauszufinden, warum es still bleibt. (Im Prüfstand ist das
            // der Normalfall: dieser Chromium kann die Tonspur der
            // Vorschau-Datei nicht dekodieren.)
            if (fehler && fehler.name !== 'AbortError') {
              freigeschaltet.current.delete(id);
              element.dataset.frei = 'abgelehnt';
              element.dataset.grund = fehler.name;
              return;
            }
            // Klingen soll es erst, wenn seine Stelle im Schnitt dran ist.
              if (!fehler && !hoerbar.current.has(id)) halte(element);
              element.dataset.frei = 'ja';
            },
            true,
          );
        }
      }),
    [],
  );

  /**
   * Der Vermerk des Freischalters überlebt einen Austausch des Elements.
   *
   * Beim Abspielen übernimmt der Film und gibt beim Anhalten wieder ab
   * (7.71d). Was danach dasteht, ist ein *neues* DOM-Element: die Erlaubnis
   * für diese Sitzung ist längst geholt (`freigeschaltet` merkt sie sich),
   * aber `data-frei` stand auf dem alten Element und ist mit ihm gegangen.
   * Der Gerätebefund läse dann „nicht freigeschaltet“, wo alles in Ordnung
   * ist -- und genau solche Zahlen kosten beim Suchen eine Runde.
   *
   * Ohne Abhängigkeiten: es gehört zu jedem Durchlauf, in dem ein Element
   * neu dastehen kann.
   */
  useEffect(() => {
    const stempeln = (kennung: string, element: HTMLVideoElement) => {
      if (element.dataset.frei || !freigeschaltet.current.has(kennung)) return;
      const vermerk = freiVermerk.current.get(kennung);
      element.dataset.frei = vermerk?.frei ?? 'ja';
      if (vermerk) element.dataset.freiStumm = vermerk.freiStumm;
    };
    for (const [clipId, element] of fremdeBilder.current) {
      if (element.dataset.src) stempeln(`ebene-${clipId}:${element.dataset.src}`, element);
    }
    for (const [clipId, element] of vorabBilder.current) {
      if (element.dataset.src) stempeln(`vorab-${clipId}:${element.dataset.src}`, element);
    }
  });

  // Was gerade klingen soll, wird gesteuert; angelegt wird oben.
  //
  // Zwei Wege, und der erste ist der gute: kann die Tonkette den Clip, läuft
  // er durch sie -- eine Quelle, die man startet, ohne fragen zu müssen. Nur
  // was sie nicht dekodieren konnte, geht weiter über ein Element (7.39).
  useEffect(() => {
    const pool = audioPool.current;
    // Der Film trägt den fertigen Ton. Alles andere schweigt, sonst klingt
    // jede Stelle zweimal (7.71).
    if (film) {
      for (const id of tonkette.klingt()) tonkette.stoppe(id);
      for (const element of pool.values()) halte(element, 'film');
      return;
    }
    // Steht die Wiedergabe, ist jeder Clip wieder frei: wer noch einmal auf
    // Abspielen tippt, will ihn noch einmal hören (7.39).
    if (!playing) tonkette.vergissAlles();
    const wanted = audible.filter((a) => tonSpuren.has(a.trackId));
    const dateiVon = (a: (typeof wanted)[number]): string | null =>
      a.clip.source.kind === 'asset' ? a.clip.source.assetId : null;
    const ueberKette = playing ? wanted.filter((a) => { const d = dateiVon(a); return d !== null && tonkette.bereit(d); }) : [];
    const kettenIds = new Set(ueberKette.map((a) => a.clip.id));
    // Erst anhalten, was nicht mehr klingen soll -- sonst überlagern sich zwei
    // Stellen desselben Clips.
    const { stoppen } = anpassen(tonkette.klingt(), kettenIds);
    for (const id of stoppen) tonkette.stoppe(id);
    for (const entry of ueberKette) {
      const speed = Math.min(4, Math.max(0.25, tempoJetzt(entry.clip)));
      const datei = dateiVon(entry);
      // Wie viel vom Clip noch kommt -- in Sekunden der Datei, also mit dem
      // Tempo gerechnet. Die Quelle hört damit von selbst auf, statt darauf
      // zu warten, dass hier jemand rechtzeitig anhält (7.39).
      const rest = Math.max(0, entry.clip.start + entry.clip.duration - playhead) * speed;
      // Die Lautstärke des Clips, auch bei „Ton aus“: das stellt der
      // Lautsprecher-Regler hinter dem Abgriff still, damit der Pegelmesser
      // weiter zeigt, was da ist.
      if (datei) tonkette.spiele(entry.clip.id, datei, entry.mediaTime, entry.volume, speed, rest);
    }

    // Der Rest wie bisher, über die Elemente.
    const perElement = wanted.filter((a) => !kettenIds.has(a.clip.id));
    const wantedIds = new Set(perElement.map((a) => a.clip.id));
    for (const [id, element] of pool) {
      if (!wantedIds.has(id)) halte(element, kettenIds.has(id) ? 'kette' : 'still');
    }
    for (const entry of perElement) {
      const element = pool.get(entry.clip.id);
      if (!element) continue;
      element.volume = monitorMuted ? 0 : Math.min(1, entry.volume);
      // Was der Clip sollte, auch wenn das Element es nicht kann: das ist die
      // Zahl, die der Durchgang prüft, und die Tonkette bekommt dieselbe.
      element.dataset.pegel = entry.volume.toFixed(2);
      const speed = Math.min(4, Math.max(0.25, tempoJetzt(entry.clip)));
      if (playing) {
        const { rate, springen } = angleichen(entry.mediaTime, element.currentTime, speed);
        setzeTempo(element, rate);
        if (springen) element.currentTime = entry.mediaTime;
      } else {
        setzeTempo(element, speed);
        if (Math.abs(element.currentTime - entry.mediaTime) > SEEK_TOLERANCE) {
          element.currentTime = entry.mediaTime;
        }
      }
      if (playing) starte(element);
      if (!playing) halte(element, 'pause');
    }
  }, [audible, assetById, playing, monitorMuted, tonSpuren, film, playhead]);

  useEffect(() => {
    const pool = audioPool.current;
    return () => {
      for (const element of pool.values()) {
        halte(element);
        element.remove();
      }
      pool.clear();
    };
  }, []);

  const duration = timelineDuration(timeline);

  /**
   * Was man antippt, ist gewählt.
   *
   * Bisher ging das nur über die Zeitleiste: auf dem Bild lag zwar die
   * Bauchbinde, aber anfassen konnte man sie erst, nachdem man sie unten
   * gefunden hatte. Die Ebenen selbst nehmen keine Tipper an (sonst kämen sie
   * nie beim Video an), also wird hier von Hand gesucht — von oben nach unten,
   * denn was obenauf liegt, meint man.
   */
  // Hebt der Finger ab, bevor die Anfasser den hinterlegten Griff geholt
  // haben, war es ein Tipp -- und der Griff darf nicht liegen bleiben und
  // später einen Zug auslösen, den niemand angefangen hat.
  useEffect(() => {
    const loesen = (): void => {
      if (useEditor.getState().griff) setGriff(null);
    };
    window.addEventListener('pointerup', loesen);
    window.addEventListener('pointercancel', loesen);
    return () => {
      window.removeEventListener('pointerup', loesen);
      window.removeEventListener('pointercancel', loesen);
    };
  }, [setGriff]);

  /** Welche Ebene liegt unter diesem Punkt? Die oberste gewinnt. */
  const clipUnter = (x: number, y: number): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ebenen = [...canvas.querySelectorAll<HTMLElement>('[data-clip]')].reverse();
    const treffer = ebenen.find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    });
    return treffer?.getAttribute('data-clip') ?? null;
  };

  /**
   * Der lange Druck -- in der Aufnahmephase, weil die Anfasser den Zug für
   * sich behalten (`stopPropagation`). Sonst gäbe es das Menü überall außer
   * auf der Ebene, die gerade gewählt ist, und das wäre die falsche.
   * Bewegt sich der Finger, war es ein Zug und kein Druck.
   */
  const druckBeginnt = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (role === 'viewer' || event.button === 2) return;
    const clipId = clipUnter(event.clientX, event.clientY);
    if (!clipId) return;
    if (druck.current) window.clearTimeout(druck.current.timer);
    const { clientX, clientY } = event;
    druck.current = {
      x: clientX,
      y: clientY,
      clipId,
      timer: window.setTimeout(() => {
        druck.current = null;
        setMenu({ clipId, x: clientX, y: clientY, at: useEditor.getState().playhead });
      }, LANGER_DRUCK),
    };
  };
  const druckBewegt = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const d = druck.current;
    if (!d) return;
    if (Math.hypot(event.clientX - d.x, event.clientY - d.y) < 6) return;
    window.clearTimeout(d.timer);
    druck.current = null;
  };
  const druckEndet = (): void => {
    if (!druck.current) return;
    window.clearTimeout(druck.current.timer);
    druck.current = null;
  };

  const waehleUnterDemFinger = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (role === 'viewer') return;
    // Die Anfasser gehören dem gewählten Clip; ein Griff daran ist kein Tipp
    // auf das Bild darunter.
    if ((event.target as Element).closest('.handles')) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const x = event.clientX;
    const y = event.clientY;
    const ebenen = [...canvas.querySelectorAll<HTMLElement>('[data-clip]')].reverse();
    const treffer = ebenen.find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    });
    const id = treffer?.getAttribute('data-clip');
    if (!id) {
      // „Nichts getroffen“ heisst zweierlei, und nur eines davon ist ein Tipp
      // auf eine leere Stelle. Läuft der vorgerechnete Film, steht überhaupt
      // keine Ebene im Dokument (7.71d) -- dann ist hier nicht zu sehen, was
      // darunter liegt, und die Auswahl bleibt, wie sie war. Sonst verliert
      // man beim Abspielen die Ebene, die man vorher angefasst hatte.
      if (film) return;
      if (selection.length) select([]);
      return;
    }
    if (selection.length === 1 && selection[0] === id) return;
    select([id]);
    // Und der Zug geht gleich weiter: die Anfasser gibt es in diesem
    // Augenblick noch nicht, also bekommen sie den angefangenen Griff
    // hinterlegt und holen ihn sich, sobald sie stehen. Sonst müsste man den
    // Finger anheben und ein zweites Mal aufsetzen (7.6a).
    setGriff({ clipId: id, pointerId: event.pointerId, x: event.clientX, y: event.clientY });
  };

  return (
    <div className="stage" ref={stage}>
      {/* Die Tonelemente der Tonspuren. Sie zeigen nichts, aber sie stehen im
          Dokument, bevor der Kopf sie erreicht -- siehe ton.ts. */}
      <div className="ton-halter" ref={tonHalter} aria-hidden="true" hidden />
      <div
        ref={canvasRef}
        className="canvas"
        style={{ aspectRatio: `${width} / ${height}` }}
        onPointerDown={waehleUnterDemFinger}
        onPointerDownCapture={druckBeginnt}
        onPointerMoveCapture={druckBewegt}
        onPointerUpCapture={druckEndet}
        onPointerCancelCapture={druckEndet}
        onContextMenu={(e) => {
          if (role === 'viewer') return;
          const clipId = clipUnter(e.clientX, e.clientY);
          if (!clipId) return;
          e.preventDefault();
          setMenu({ clipId, x: e.clientX, y: e.clientY, at: useEditor.getState().playhead });
        }}
      >
        {/* Die Leinwand, die selbst malt (7.69a). Sie liegt unter allem und
            zeigt vorerst nur die Basisspur; die Ebenen darüber sind weiter
            Elemente. Ohne `?gl=1` steht sie gar nicht da. */}
        {GL_GEWUENSCHT && !film ? <canvas className="gl-leinwand" ref={glLeinwand} aria-hidden="true" /> : null}
        {/* Warten ist kein Fehler, aber es soll dastehen (7.69o). */}
        {puffert ? (
          <div className="puffert" data-puffert>
            Lädt…
          </div>
        ) : null}
        {/* 7.71: Der vorgerechnete Film. Ein Videoelement statt der beiden
            Basisflächen, aller Ebenen, aller Tonelemente und der Untertitel --
            die stecken alle schon darin. Auf dem Telefon ist das der
            Unterschied zwischen einem Dekoder und sechs. */}
        {/* Zwei Elemente, wie auf der Basisspur: eines spielt den laufenden
            Abschnitt, im anderen wartet der nächste schon geladen. Ohne das
            kostet jeder Wechsel einen Ladevorgang (7.71b). Das wartende
            steht da, ist aber nicht zu sehen. */}
        {stuecke.length > 0
          ? [0, 1].map((slot) => (
              <video
                key={slot}
                ref={slot === 0 ? filmA : filmB}
                className="film-vorschau"
                data-film={film && slot === stueckNr % 2 ? film.id : 'vorlader'}
                data-film-wartet={slot === stueckNr % 2 ? undefined : 'ja'}
                style={slot === stueckNr % 2 ? undefined : { opacity: 0, pointerEvents: 'none' }}
                playsInline
                preload="auto"
                disablePictureInPicture
                onError={
                  film && slot === stueckNr % 2
                    ? () => setMediaError('Der gerechnete Film liess sich nicht laden. „Film“ ausschalten oder neu rechnen.')
                    : undefined
                }
                onLoadedData={() => setMediaError(null)}
                onSeeking={merkeSuchen}
                onSeeked={merkeGesucht}
              />
            ))
          : null}
        {film ? null : (
          <>
        {/* **Die Farbe eines Übergangs durch Weiss oder Schwarz** (7.161).
            Die Leinwand malt sie unter die beiden Clips (7.149); ohne
            Leinwand fehlte sie, und durch Weiss ging es hier durch Schwarz --
            den Grund der Fläche. */}
        {(() => {
          const farbe = GL_GEWUENSCHT ? undefined : base.find((b) => b.dipFuellung)?.dipFuellung;
          return farbe ? (
            <div data-dip-fuellung={farbe} style={{ position: 'absolute', inset: 0, background: farbe, pointerEvents: 'none' }} />
          ) : null;
        })()}
        {[0, 1].map((slot) => {
          const entry = base.find((b) => b.slot === slot);
          // Ein Standbild hat gar keine Vorschau-Datei -- es bekommt beim
          // Aufbereiten nur einen Poster. Das Element bliebe leer, und die
          // Leinwand schwarz; das Bild kommt weiter unten als eigene Ebene.
          const still = entry ? istStandbild(assetById, entry.clip) : false;
          const generated = (entry && entry.clip.source.kind !== 'asset') || still;
          // Die Form des Übergangs nur ohne Leinwand: malt die Leinwand, liegt
          // das Element darunter und muss ganz gezeichnet werden (iOS liefert
          // sonst keine Bilder, siehe unten).
          const form = entry && !generated && !GL_GEWUENSCHT ? formImStapel(entry, base) : null;
          const masse = entry ? (videoDims[slot] ?? assetDims(assetById, entry.clip)) : undefined;
          return (
            <video
              key={slot}
              ref={slot === 0 ? videoA : videoB}
              // playsInline is what keeps iOS from taking the video fullscreen.
              playsInline
              preload="auto"
              disablePictureInPicture
              /* Das Standbild, solange noch kein Einzelbild dekodiert ist.
                 Ohne das ist die Fläche schwarz, während die Datei geholt
                 wird -- und schwarz sieht aus wie „kaputt“ und nicht wie
                 „gleich“. Der Browser blendet es von selbst weg, sobald das
                 erste Bild da ist. */
              poster={
                entry && !generated && entry.clip.source.kind === 'asset'
                  ? (assetById.get(entry.clip.source.assetId)?.posterUrl ?? undefined)
                  : undefined
              }
              // A proxy that will not decode must say so: otherwise the preview
              // is a black rectangle and everything looks broken for no reason.
              onError={(e) => {
                const code = (e.currentTarget as HTMLVideoElement).error?.code;
                setMediaError(
                  code === 4
                    ? 'Dieses Gerät kann die Vorschau nicht abspielen. Der Export geht trotzdem.'
                    : 'Die Vorschau konnte nicht geladen werden.',
                );
              }}
              onLoadedData={() => setMediaError(null)}
              onSeeking={merkeSuchen}
              onSeeked={merkeGesucht}
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (v.videoWidth && v.videoHeight) setVideoDims((d) => ({ ...d, [slot]: { w: v.videoWidth, h: v.videoHeight } }));
                // Erst jetzt darf gesucht werden. Und gesucht werden *muss*:
                // Safari auf dem iPhone malt das erste Bild nicht von selbst,
                // sondern erst nach einem Sprung -- ohne den steht dort ein
                // schwarzes Rechteck, bis man abspielt.
                const want = Number(v.dataset.want ?? '0');
                v.currentTime = Number.isFinite(want) && want > 0 ? want : 0.001;
              }}
              data-clip={entry && !generated ? entry.clip.id : undefined}
              style={{
                // Malt die Leinwand selbst, bleibt das Element sichtbar --
                // und liegt *unter* der deckenden Leinwand. Unsichtbar
                // (opacity 0) bekam es auf iOS kaum noch neue Bilder für die
                // Grafikkarte: ein Bild je Sekunde (Sascha, 8. September:
                // „Videos werden in der Vorschau mit einem FPS abgespielt“).
                // WebKit liefert Bilder nur für Videos, die es zeichnet.
                opacity: entry && !generated ? (form ? form.deckung : deckungImStapel(entry, base)) : 0,
                filter: GL_GEWUENSCHT ? undefined : entry ? cssFilter(entry.clip, true) : undefined,
                ...(entry && !generated
                  ? baseVideoBox(entry.clip, playhead, masse, width / height, GL_GEWUENSCHT ? 0 : (entry.schub ?? 0))
                  : {}),
                ...(entry && form?.maske && masse
                  ? uebergangsMaskenStil(form.maske.art, form.maske.p, form.maske.umgekehrt, width / height,
                      kastenAnteil(masse, width / height), transformAt(entry.clip, playhead - entry.clip.start))
                  : {}),
              }}
            />
          );
        })}

        {/* A look on a base clip is an SVG filter its element points at;
            the vignette is a layer of its own, since CSS has none. */}
        {/* Standbilder auf der Basisspur: derselbe Kasten wie ein Video, nur
            aus dem Poster. Ohne das blieb die Leinwand schwarz, obwohl der
            Clip in der Zeitleiste sein Bild zeigte (Saschas Fund vom
            5. September). */}
        {base
          .filter((b) => istStandbild(assetById, b.clip))
          .map((b) => {
            const asset = b.clip.source.kind === 'asset' ? assetById.get(b.clip.source.assetId) : undefined;
            if (!asset?.posterUrl) return null;
            return (
              <img
                key={`still-${b.clip.id}`}
                data-clip={b.clip.id}
                src={asset.posterUrl}
                alt=""
                ref={(el) => meldeStandbild(b.clip.id, el)}
                onLoad={() => male()}
                style={{
                  position: 'absolute',
                  opacity: GL_GEWUENSCHT ? 0 : b.opacity,
                  filter: GL_GEWUENSCHT ? undefined : cssFilter(b.clip),
                  objectFit: 'contain',
                  ...baseVideoBox(b.clip, playhead, assetDims(assetById, b.clip), width / height),
                }}
              />
            );
          })}

        {base.map((b) => {
          const lut = lutOf(b.clip.effects);
          return lut ? <LookFilter key={`look-${b.clip.id}`} clipId={b.clip.id} name={lut.name} intensity={lut.intensity} /> : null;
        })}

        {base.map((b) => {
          const r = raederOf(b.clip.effects);
          return r ? <FarbraederFilter key={`rad-${b.clip.id}`} clipId={b.clip.id} tiefen={r.tiefen} mitten={r.mitten} hoehen={r.hoehen} /> : null;
        })}

        <KanteFilter />

        {/* Das Gamma als eigener Filter (7.133): CSS kann es nicht, und die
            Vorschau liess es bis dahin ganz fallen -- während der Film es
            anwandte. */}
        {base.map((b) => {
          const g = gammaOf(b.clip.effects);
          return g !== 1 ? <GammaFilter key={`gamma-${b.clip.id}`} clipId={b.clip.id} gamma={g} /> : null;
        })}

        {base
          .filter((b) => b.clip.source.kind !== 'asset')
          .map((b) => (
            <GeneratedLayer
              key={b.clip.id}
              clip={b.clip}
              opacity={b.opacity}
              playhead={playhead}
              brandKit={brandKit}
              logoUrl={brandLogoUrl}
              height={height}
            />
          ))}

        {/* Under the overlays, over whatever the base track painted. */}
        {base
          .filter((b) => vignetteAmount(b.clip) > 0)
          .map((b) => (
            <div
              key={`vignette-${b.clip.id}`}
              className="vignette-layer"
              style={{
                opacity: GL_GEWUENSCHT ? 0 : b.opacity,
                background: vignetteVerlauf(vignetteAmount(b.clip)),
              }}
            />
          ))}

        <div className="overlay-layer">
          {overlays.map((o) => {
            const lut = lutOf(o.clip.effects);
            return lut ? <LookFilter key={`look-${o.clip.id}`} clipId={o.clip.id} name={lut.name} intensity={lut.intensity} /> : null;
          })}
          {overlays.map((o) => {
            const r = raederOf(o.clip.effects);
            return r ? <FarbraederFilter key={`rad-${o.clip.id}`} clipId={o.clip.id} tiefen={r.tiefen} mitten={r.mitten} hoehen={r.hoehen} /> : null;
          })}
          {overlays.map((o) => {
            const g = gammaOf(o.clip.effects);
            return g !== 1 ? <GammaFilter key={`gamma-${o.clip.id}`} clipId={o.clip.id} gamma={g} /> : null;
          })}
          {/* Sichtbare Ebenen und die, die gleich kommen, im selben Baum mit
              demselben Schlüssel: so bleibt es *dasselbe* Element, wenn aus
              dem Vorlader die Ebene wird -- und die Erlaubnis, die die
              Berührung geholt hat, bleibt daran (7.69m). */}
          {[
            ...overlays.map((o) => ({ ...o, vorab: false, vorladen: 'auto' as const })),
            ...baldOverlays
              .filter((b) => !overlays.some((o) => o.clip.id === b.clip.id))
              .map((b) => ({
                clip: b.clip,
                mediaTime: b.mediaTime,
                opacity: 0,
                vorab: true,
                vorladen: b.vorladen,
                // Schon beim Vorladen der richtige Modus: so ist es dasselbe
                // Element, wenn aus dem Vorlader die Ebene wird.
                mischmodus:
                  timeline.tracks.find((t) => t.clips.some((c) => c.id === b.clip.id))?.mischmodus ?? 'normal',
              })),
          ].map((o) => {
            const ton = o.vorab ? undefined : audible.find((a) => a.clip.id === o.clip.id);
            // **Die Form eines Übergangs auf einer oberen Spur** (7.169):
            // dieselbe Maske wie auf der Basisspur (7.161), hier am Umschlag
            // und über die ganze Leinwand. Die kommende Ebene liegt oben und
            // deckt voll; die Kante macht den Übergang. Nur ohne Leinwand --
            // die malt die Form selbst.
            const form = !GL_GEWUENSCHT && !o.vorab ? (o as { uebergang?: LaufenderUebergang }).uebergang : undefined;
            // S10: Wie die Spur auf der darunter liegt. Der Umschlag steht
            // *immer* da, auch bei „Normal“ -- sonst wechselte beim Umschalten
            // die Form des Baums, und das Medienelement darin würde neu
            // gebaut. Mit ihm ginge die Erlaubnis verloren, die die Berührung
            // geholt hat (7.69m), und die Vorschau bliebe stumm stehen.
            return (
              <div
                key={o.clip.id}
                data-mischebene={o.mischmodus}
                data-uebergang-maske={form ? form.art : undefined}
                style={{
                  mixBlendMode: MISCH_CSS[o.mischmodus] as 'normal',
                  // Mit Maske braucht der Umschlag die Grösse der Leinwand --
                  // ohne Kasten gäbe es nichts, worauf sie liegt. Die Ebenen
                  // darin stehen dann wie vorher, denn er deckt die Leinwand
                  // genau. Tipps gehen weiter an die Leinwand.
                  ...(form
                    ? {
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        ...uebergangsMaskenStil(form.art, form.p, false, width / height, { w: 100, h: 100 }, { x: 0.5, y: 0.5, scale: 1 }),
                      }
                    : {}),
                }}
              >
              <OverlayLayer
                clip={o.clip}
                opacity={form ? form.deckung : o.opacity}
                mediaTime={o.mediaTime}
                playhead={playhead}
                asset={o.clip.source.kind === 'asset' ? assetById.get(o.clip.source.assetId) : undefined}
                brandKit={brandKit}
                logoUrl={brandLogoUrl}
                height={height}
                playing={playing && !o.vorab}
                lautstaerke={ton && !monitorMuted ? ton.volume : ton ? 0 : null}
                melde={meldeBild}
                meldeVorab={meldeVorab}
                meldeStandbild={meldeStandbild}
                nachgeladen={male}
                vorab={o.vorab}
                vorladen={o.vorladen}
              />
              </div>
            );
          })}
          {handled ? <CanvasHandles clip={handled} canvas={canvasRef.current} /> : null}
          {/* Raster und Schutzbereich (S25). Sie liegen dauerhaft da, ohne
              Auswahl und ohne Ziehen -- danach richtet man ein Bild aus, nicht
              beim Schieben. Die Linien beim Ziehen (`fang-raster`) bleiben
              davon unberührt: die erklären, *warum* etwas stehen bleibt. */}
          <Untertitel subtitles={timeline.subtitles} height={height} />
          {rasterWahl !== 'aus' ? (
            <div className="raster" aria-hidden="true" data-raster={rasterWahl}>
              {rasterLinien(rasterWahl).map((linie) => (
                <span
                  key={`rx-${linie.bei}`}
                  className={`raster-linie senkrecht ${linie.art}`}
                  style={{ left: `${linie.bei * 100}%` }}
                />
              ))}
              {rasterLinien(rasterWahl).map((linie) => (
                <span
                  key={`ry-${linie.bei}`}
                  className={`raster-linie waagerecht ${linie.art}`}
                  style={{ top: `${linie.bei * 100}%` }}
                />
              ))}
            </div>
          ) : null}
        </div>
          </>
        )}

        {menu ? (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={clipMenuItems(
            {
              projectId: projektId ?? '',
              refreshAssets,
              timeline,
              settings: timeline.settings,
              fps: timeline.settings.fps,
              assetById,
              selection,
              dispatch,
              toast,
              select,
              setMultiSelect,
              setPlayhead: setPlayheadStore,
              setErsetzen,
              setClipName,
              setAngaben,
              setHinweisAnker,
              onOpenSheet: (blatt) => oeffneBlatt?.(blatt),
              tonkurven,
              tonkurveUmschalten,
            },
            menu.clipId,
            // Der genaue Kopf im Augenblick des Drucks: „hier trennen“ meint
            // die Stelle, an der man ihn sieht, und nicht die zusammengefasste
            // aus dem Bild-Takt.
            menu.at,
          )}
          onClose={() => setMenu(null)}
        />
      ) : null}

      {duration === 0 ? (
          <LeererZustand
            zeichen="film"
            satz="Noch nichts geschnitten."
            dazu="Such dir ein Medium aus — es landet dann in der Zeitleiste, und von dort geht alles Weitere."
            knopf={oeffneMedien ? { text: 'Medien öffnen', onClick: oeffneMedien } : undefined}
            kennung="buehne"
          />
        ) : null}

        {mediaError ? (
          <div className="empty-stage" style={{ position: 'absolute', inset: 'auto 8px 8px', fontSize: 12 }}>
            {mediaError}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Was die Basisspur selbst zeichnet: Farbe, Text, Markeninhalt.
 *
 * Der Kopf gehört dazu, weil auch hier Keyframes gelten. Ohne ihn stand die
 * Ebene stur auf ihrem festen Transform: das Ziehen wirkte (die Änderung war
 * im Schnitt und im Rückgängig-Knopf), aber das Bild rührte sich nicht.
 */
function GeneratedLayer({
  clip,
  opacity,
  playhead,
  brandKit,
  logoUrl,
  height,
}: {
  clip: Clip;
  opacity: number;
  playhead: number;
  brandKit: BrandKit | undefined;
  logoUrl: string | null;
  height: number;
}): ReactElement | null {
  // Wo die Ebene *jetzt* steht: Keyframes bewegen sie, sonst gilt der feste
  // Transform. Dieselbe Rechnung wie bei den Ebenen darüber und im Export.
  const tf = transformAt(clip, playhead - clip.start);
  if (clip.source.kind === 'color') {
    return (
      <div
        data-clip={clip.id}
        style={{ position: 'absolute', inset: 0, background: clip.source.color, opacity: GL_GEWUENSCHT ? 0 : opacity, filter: cssFilter(clip, true) }}
      />
    );
  }
  if (clip.source.kind === 'text') {
    return (
      <div style={{ position: 'absolute', inset: 0, opacity: GL_GEWUENSCHT ? 0 : opacity }}>
        <TextLayer
          clipId={clip.id}
          text={clip.source.text}
          style={clip.source.style}
          x={tf.x}
          y={tf.y}
          rotate={tf.rotate}
          scale={tf.scale}
          height={height}
        />
      </div>
    );
  }
  if (clip.source.kind === 'brand') {
    return (
      <BrandLayer
        clip={clip}
        opacity={opacity}
        brandKit={brandKit}
        logoUrl={logoUrl}
        height={height}
        x={tf.x}
        y={tf.y}
        scale={tf.scale}
        rotate={tf.rotate}
      />
    );
  }
  return null;
}

function OverlayLayer({
  clip,
  opacity,
  mediaTime,
  playhead,
  asset,
  brandKit,
  logoUrl,
  height,
  playing,
  lautstaerke,
  melde,
  meldeVorab,
  meldeStandbild,
  nachgeladen,
  vorab = false,
  vorladen = 'auto',
}: {
  clip: Clip;
  opacity: number;
  mediaTime: number;
  playhead: number;
  asset: AssetView | undefined;
  brandKit: BrandKit | undefined;
  logoUrl: string | null;
  height: number;
  playing: boolean;
  /** Null heißt: dieser Clip klingt hier nicht. 0 heißt: er ist abgedreht. */
  lautstaerke: number | null;
  melde: (clipId: string, element: HTMLVideoElement | null) => void;
  /** Für die Leinwand, die selbst malt: wo das Standbild dieser Ebene hängt. */
  meldeStandbild?: (clipId: string, element: HTMLImageElement | null) => void;
  /** Ein Bild ist nachgeladen -- die Leinwand soll noch einmal malen. */
  nachgeladen?: () => void;
  /** Vorlader für die Videoebenen (7.69m). */
  meldeVorab?: (clipId: string, element: HTMLVideoElement | null) => void;
  /** Noch nicht dran: Element da, Datei geladen, unsichtbar und angehalten. */
  vorab?: boolean;
  vorladen?: 'auto' | 'none';
}): ReactElement | null {
  const slide = slideOffset(clip.animation, playhead - clip.start);
  // Ein Vorlader ist nur ein Videoelement -- Text, Marke und Standbild
  // brauchen keine Erlaubnis und keinen Vorlauf.
  if (vorab && !(clip.source.kind === 'asset' && asset?.kind === 'video' && asset.proxyUrl)) return null;
  // Where the layer is *now*: keyframes move it, the static transform otherwise.
  const tf = transformAt(clip, playhead - clip.start);
  if (clip.source.kind === 'text') {
    return (
      <TextLayer
        clipId={clip.id}
        text={clip.source.text}
        style={clip.source.style}
        x={tf.x + slide.dx}
        y={tf.y + slide.dy}
        rotate={tf.rotate}
        scale={tf.scale}
        opacity={opacity}
        height={height}
      />
    );
  }
  if (clip.source.kind === 'brand') {
    return (
      <BrandLayer
        clip={clip}
        opacity={opacity}
        brandKit={brandKit}
        logoUrl={logoUrl}
        height={height}
        x={tf.x + slide.dx}
        y={tf.y + slide.dy}
        scale={tf.scale}
        rotate={tf.rotate}
      />
    );
  }
  if (clip.source.kind === 'color') {
    return <FarbEbene clip={clip} opacity={opacity} tf={tf} slide={slide} />;
  }
  if (clip.source.kind === 'asset' && asset?.kind === 'image' && asset.posterUrl) {
    return (
      <img
        data-clip={clip.id}
        src={asset.posterUrl}
        alt=""
        ref={(el) => meldeStandbild?.(clip.id, el)}
        onLoad={() => nachgeladen?.()}
        style={{
          position: 'absolute',
          width: '100%',
          opacity: GL_GEWUENSCHT ? 0 : opacity,
          filter: GL_GEWUENSCHT ? undefined : cssFilter(clip),
          ...ebenenLage({ x: tf.x + slide.dx, y: tf.y + slide.dy, scale: tf.scale, rotate: tf.rotate }),
        }}
      />
    );
  }
  // Ein Video auf einer Ebene über der Basisspur. Hier stand bis heute ein
  // Standbild -- „damit die Vorschau kein drittes Videoelement je Spur
  // braucht“. Nur: dann läuft es eben nicht, und man sieht dem Bild nicht an,
  // dass es nur ein Bild ist. Es bekommt sein eigenes Element; gebaut wird
  // eines je *sichtbarer* Ebene, nicht je Spur, und die sind an einer Stelle
  // im Schnitt selten mehr als ein, zwei.
  if (clip.source.kind === 'asset' && asset?.kind === 'video' && asset.proxyUrl) {
    return (
      <OverlayVideo
        clip={clip}
        asset={asset}
        opacity={vorab ? 0 : opacity}
        mediaTime={mediaTime}
        playing={playing && !vorab}
        lautstaerke={vorab ? null : lautstaerke}
        melde={melde}
        meldeVorab={meldeVorab}
        mitTon={!clip.muted && Boolean(asset.hasAudio)}
        vorab={vorab}
        vorladen={vorladen}
        x={tf.x + slide.dx}
        y={tf.y + slide.dy}
        scale={tf.scale}
        rotate={tf.rotate}
      />
    );
  }
  // Alles andere mit einem Standbild -- ein Ton auf einer Bildspur etwa, oder
  // ein Video, dessen Vorschau-Datei noch nicht fertig ist.
  if (clip.source.kind === 'asset' && asset?.posterUrl) {
    return (
      <img
        data-clip={clip.id}
        src={asset.posterUrl}
        alt=""
        title={`${asset.name} bei ${mediaTime.toFixed(1)}s`}
        ref={(el) => meldeStandbild?.(clip.id, el)}
        onLoad={() => nachgeladen?.()}
        style={{
          position: 'absolute',
          width: '100%',
          opacity: GL_GEWUENSCHT ? 0 : opacity * 0.95,
          filter: GL_GEWUENSCHT ? undefined : cssFilter(clip, asset.kind === 'video'),
          ...ebenenLage({ x: tf.x + slide.dx, y: tf.y + slide.dy, scale: tf.scale, rotate: tf.rotate }),
        }}
      />
    );
  }
  return null;
}

/**
 * Ein Video auf einer Ebene über der Basisspur.
 *
 * Es läuft nach derselben Regel wie die Basisspur: beim Stehen und Suchen gibt
 * der Kopf die Medienzeit vor, beim Abspielen holt es den Abstand über das
 * Tempo auf statt per Sprung -- ein Sprung setzt auf dem Telefon die
 * Dekodierung neu auf, und dann steht das Bild.
 *
 * Den Takt gibt es nicht an: das bleibt bei der Basisspur. Eine Ebene, die
 * die Zeit vorgibt, würde beim Hinein- und Herausschneiden den Kopf
 * übernehmen und wieder abgeben, und niemand könnte danach sagen, welche Uhr
 * gerade gilt.
 */
/**
 * Eine Farbfläche als Ebene.
 *
 * Als eigener Bestandteil, weil sie einen Haken braucht (`useMaskenForm`) und
 * ein Haken nicht in einem Zweig stehen darf. Vorher war sie ein `div`
 * mitten in `OverlayLayer` -- und genau deshalb hatte sie keine Form (7.138).
 */
function FarbEbene({
  clip,
  opacity,
  tf,
  slide,
}: {
  clip: Clip;
  opacity: number;
  tf: { x: number; y: number; scale: number; rotate: number };
  slide: { dx: number; dy: number };
}): ReactElement {
  const form = useMaskenForm(clip);
  return (
    <>
      {form.defs}
      <KonturFlaeche
        clipId={clip.id}
        kontur={form.kontur}
        hoehe="100%"
        stil={{ opacity: GL_GEWUENSCHT ? 0 : opacity, ...ebenenLage({ x: tf.x + slide.dx, y: tf.y + slide.dy, scale: tf.scale, rotate: tf.rotate }) }}
      />
      <div
        data-clip={clip.id}
        data-maske={clip.maske?.form && clip.maske.form !== 'keine' ? clip.maske.form : undefined}
        ref={form.mess}
        style={{
          position: 'absolute',
          // Kein Seitenverhältnis, sondern der ganze Rahmen (7.103) -- wie auf
          // der Basisspur und wie im Export.
          width: '100%',
          height: '100%',
          background: clip.source.kind === 'color' ? clip.source.color : undefined,
          opacity: GL_GEWUENSCHT ? 0 : opacity,
          filter: cssFilter(clip, true),
          // Die Form, in der sie läuft (7.82/7.138) -- der Export schneidet
          // sie, also schneidet sie hier auch.
          clipPath: form.clipPath,
          // Und mit ihrem Winkel: der Export dreht jede Ebene, auch eine, die
          // nur aus Farbe besteht.
          ...ebenenLage({ x: tf.x + slide.dx, y: tf.y + slide.dy, scale: tf.scale, rotate: tf.rotate }),
        }}
      >
        {/* Die Vignette als Kind (7.172): so gilt die Deckung der Fläche und
            ihre Form auch für sie, wie im Film. */}
        {!GL_GEWUENSCHT && vignetteAmount(clip) > 0 ? (
          <div className="vignette-layer" style={{ background: vignetteVerlauf(vignetteAmount(clip)) }} />
        ) : null}
      </div>
    </>
  );
}

/**
 * Die Form, in der eine Ebene läuft (7.82) -- als `<clipPath>` samt Defs, und
 * zwar für **jede** Art von Ebene.
 *
 * **Bis zum 22. September stand das nur im Videozweig** (7.138): eine
 * Farbfläche mit einer Form zeigte im Dokument ihr ganzes Rechteck, während
 * der Film sie schnitt -- und die Leinwand kannte Formen ohnehin nicht.
 * Dieselbe Klasse wie 7.127 („eine Farbfläche liess sich nicht drehen“): ein
 * Zweig, der eine Eigenschaft überspringt, während der Export sie hat.
 *
 * Ausgemessen wird die Ebene, weil ein Kreis auf einem breiten Bild sonst zur
 * Ellipse würde -- `svgFormen` rechnet mit der wahren Grösse, genau wie der
 * Ausdruck im Export mit `W`/`H`.
 */
function useMaskenForm(clip: Clip): {
  mess: (el: Element | null) => void;
  clipPath: string | undefined;
  defs: ReactElement | null;
  kontur: { farbe: string; clipPath: string; hoehe: number } | null;
} {
  const [mass, setMass] = useState({ b: 1, h: 1 });
  const beobachter = useRef<ResizeObserver | null>(null);
  const aktiv = !!clip.maske && clip.maske.form !== 'keine';
  // Wird an den Anfassern der Form gezogen (7.224), schneidet die Vorschau
  // schon dort, wo der Finger ist.
  const entwurf = useEditor((s) => (s.maskeEntwurf?.clipId === clip.id ? s.maskeEntwurf : null));
  const maske = aktiv && entwurf ? { ...clip.maske!, x: entwurf.x, y: entwurf.y, groesse: entwurf.groesse, drehung: entwurf.drehung } : clip.maske;
  const mess = useCallback(
    (el: Element | null) => {
      beobachter.current?.disconnect();
      beobachter.current = null;
      if (!el || !aktiv) return;
      // Die Grösse der Ebene selbst, nicht ihr Rechteck auf dem Schirm
      // (7.186): gedreht ist das die Hülle, und die Form bekam ein anderes
      // Seitenverhältnis als im Film und auf der Leinwand.
      const messen = (): void => {
        const k = el instanceof HTMLElement ? { width: el.offsetWidth, height: el.offsetHeight } : el.getBoundingClientRect();
        if (k.width > 0 && k.height > 0) setMass((m) => (m.b === k.width && m.h === k.height ? m : { b: k.width, h: k.height }));
      };
      messen();
      const b = new ResizeObserver(messen);
      b.observe(el);
      beobachter.current = b;
    },
    [aktiv],
  );
  useEffect(() => () => beobachter.current?.disconnect(), []);
  // Mit Kontur (7.224) schneidet die Ebene selbst nur die Innenfläche; die
  // ganze Form trägt eine Fläche in der Farbe der Kontur darunter.
  const kontur = aktiv && maske!.kontur && maske!.kontur.staerke > 0 ? maske!.kontur : null;
  const formen = aktiv ? svgFormen(maske!.form, mass.b, mass.h, kontur?.staerke ?? 0, maske!.gespiegelt) : [];
  const aussen = kontur ? svgFormen(maske!.form, mass.b, mass.h, 0, maske!.gespiegelt) : [];
  // Ort, Grösse und Drehung der Form sitzen als ein `transform` um die Formen
  // herum (7.82b) -- die Formen selbst bleiben die eingespannten, genau wie
  // im Export.
  const lage = aktiv ? svgLage(maske!, mass.b, mass.h) : null;
  const id = `maske-${clip.id}`;
  const schnitt = (formenHier: typeof formen) =>
    formenHier.map((f, i) =>
      f.art === 'ellipse' ? (
        <ellipse key={i} cx={f.cx} cy={f.cy} rx={f.rx} ry={f.ry} transform={lage ?? undefined} />
      ) : (
        <rect key={i} x={f.x} y={f.y} width={f.breite} height={f.hoehe} rx={f.r} ry={(f.r * mass.b) / Math.max(mass.h, 1)} transform={lage ?? undefined} />
      ),
    );
  const defs =
    formen.length > 0 ? (
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          {kontur ? (
            <clipPath id={`${id}-kontur`} clipPathUnits="objectBoundingBox">
              {schnitt(aussen)}
            </clipPath>
          ) : null}
          <clipPath id={id} clipPathUnits="objectBoundingBox">
            {/* **Der `transform` steht an den Formen selbst** (7.139), nicht
                an einer Gruppe darum herum: ein `<g>` ist in einem
                `<clipPath>` nicht erlaubt (SVG 1.1), und der Browser
                übergeht es samt allem, was darin steht -- die Schnittfläche
                war leer und die Ebene ganz weg. Gemessen am 22. September:
                ohne den Schnitt stand die Ebene da, mit ihm nichts. */}
            {schnitt(formen)}
          </clipPath>
        </defs>
      </svg>
    ) : null;
  return {
    mess,
    clipPath: formen.length > 0 ? `url(#${id})` : undefined,
    defs,
    kontur: kontur && formen.length > 0 ? { farbe: kontur.farbe, clipPath: `url(#${id}-kontur)`, hoehe: mass.h } : null,
  };
}

/**
 * Die Fläche in der Farbe der Kontur, unter einer Ebene mit Form (7.224) --
 * gleich gross, gleich gelegen und auf die ganze Form geschnitten. Die Ebene
 * darüber zeigt nur die Innenfläche; was bleibt, ist der Strich.
 */
function KonturFlaeche({
  clipId,
  kontur,
  hoehe,
  stil,
}: {
  clipId: string;
  kontur: { farbe: string; clipPath: string; hoehe: number } | null;
  hoehe: string;
  stil: CSSProperties;
}): ReactElement | null {
  if (!kontur) return null;
  return (
    <div
      data-kontur={clipId}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 'auto', width: '100%', height: hoehe, background: kontur.farbe, clipPath: kontur.clipPath, pointerEvents: 'none', ...stil }}
    />
  );
}

function OverlayVideo({
  clip,
  asset,
  opacity,
  mediaTime,
  playing,
  lautstaerke,
  melde,
  x,
  y,
  scale,
  rotate,
  vorab,
  meldeVorab,
  mitTon = false,
  vorladen = 'auto',
}: {
  clip: Clip;
  asset: AssetView;
  opacity: number;
  mediaTime: number;
  playing: boolean;
  lautstaerke: number | null;
  melde: (clipId: string, element: HTMLVideoElement | null) => void;
  meldeVorab?: (clipId: string, element: HTMLVideoElement | null) => void;
  /** Wird diese Ebene klingen? Dann muss auch der Anlauf aus der Berührung mit Ton sein (7.37d, 7.69m). */
  mitTon?: boolean;
  vorladen?: 'auto' | 'none';
  x: number;
  y: number;
  scale: number;
  rotate: number;
  /** Nur laden und an die richtige Stelle springen -- noch nicht zeigen. */
  vorab?: boolean;
}): ReactElement {
  const ref = useRef<HTMLVideoElement>(null);
  // Die Form steht in `useMaskenForm` -- an einer Stelle für alle Arten von
  // Ebenen (7.138).
  const form = useMaskenForm(clip);

  // An- und wieder abmelden: der Freischalter braucht die Liste der Elemente,
  // die es gerade *gibt*. Ein Element, das nur vorlädt, meldet sich nicht an:
  // es soll gar nicht spielen.
  useEffect(() => {
    if (vorab) {
      // Auch der Vorlader meldet sich an -- beim Freischalter, nicht bei der
      // Wiedergabe (7.69m).
      meldeVorab?.(clip.id, ref.current);
      return () => meldeVorab?.(clip.id, null);
    }
    melde(clip.id, ref.current);
    return () => melde(clip.id, null);
  }, [clip.id, melde, meldeVorab, vorab]);
  useEffect(() => {
    if (vorab || !GL_GEWUENSCHT || !ref.current) return;
    return bildtakt(ref.current);
  }, [vorab]);

  // Ohne Abhängigkeiten, wie bei der Basisspur: der Kopf bewegt sich mit jedem
  // Bild, und dieser Abgleich gehört zu jedem Bild.
  useEffect(() => {
    const element = ref.current;
    const src = asset.proxyUrl;
    if (!element || !src) return;
    if (element.dataset.src !== src) {
      element.dataset.src = src;
      element.src = ausDemRam(src) ?? src;
    }
    void inDenRam(src);
    // Wie oben: ein currentTime vor den Kopfdaten verfällt still. Frisch aus
    // dem Kopf, nicht aus den Props (7.69i): sonst startet die Ebene bis zu
    // 200 ms daneben, und der Abgleich holt das mit Tempowechseln auf.
    element.dataset.want = String(medienzeitJetzt(clip));
    const speed = Math.min(4, Math.max(0.25, tempoJetzt(clip)));
    element.dataset.ton = mitTon ? '1' : '0';
    // Solange der Freischalter gerade anstösst, bleibt das Element in Ruhe:
    // stumm schalten mitten im Anlauf hiesse, die Erlaubnis stumm zu holen.
    if (vorab && element.dataset.frei === 'versucht') return;
    element.muted = vorab || lautstaerke === null;
    element.volume = vorab ? 0 : Math.min(1, lautstaerke ?? 0);
    if (vorab) {
      // Laden und suchen, mehr nicht. Ein Vorlader, der spielt, wäre ein
      // zweiter Ton und ein zweiter Dekoder für nichts.
      halte(element);
      if (element.readyState >= 1 && Math.abs(element.currentTime - mediaTime) > SEEK_TOLERANCE) {
        element.currentTime = mediaTime;
      }
      return;
    }
    if (playing) {
      if (taktendeEbene === clip.id) {
        // Diese Ebene *ist* die Zeit (7.69l): sie wird nicht nachgestellt.
        setzeTempo(element, speed);
      } else if (!element.seeking) {
        // Gegen die genaue Zeit, nicht gegen die aus den Props (siehe
        // medienzeitJetzt): sonst ist der Abgleich selbst der Ruckler.
        const jetzt = medienzeitJetzt(clip);
        // Seit wann die Ebene mehr als 0,15 s daneben liegt (7.191): erst
        // nach 400 ms wird nachgestellt -- ein Ausreisser unter Last ist dann
        // schon wieder weg (Bau #335: vier Tempowechsel in 1,5 s, ohne Last null).
        const nun = performance.now();
        if (Math.abs(jetzt - element.currentTime) > 0.15) {
          if (!element.dataset.ueberSeit) element.dataset.ueberSeit = String(nun);
        } else delete element.dataset.ueberSeit;
        const ueberSeit = element.dataset.ueberSeit ? nun - Number(element.dataset.ueberSeit) : 0;
        const { rate, springen } = angleichenRuhig(jetzt, element.currentTime, speed, element.playbackRate, { geduld: 400, ueberSeit });
        if (Math.abs(element.playbackRate - rate) > 0.004) zaehleBild('tempo-ebene');
        setzeTempo(element, rate);
        if (springen) springe(element, jetzt, speed, 'sprung-ebene');
      }
      starte(element);
    } else {
      setzeTempo(element, speed);
      stelleImStehen(element, mediaTime);
      halte(element);
    }
  });

  return (
    <>
      {form.defs}
      <KonturFlaeche clipId={clip.id} kontur={form.kontur} hoehe={`${form.kontur?.hoehe ?? 0}px`} stil={{ opacity, ...ebenenLage({ x, y, scale, rotate }) }} />
    <video
      ref={(el) => {
        ref.current = el;
        form.mess(el);
      }}
      data-clip={vorab ? undefined : clip.id}
      data-ebene={vorab ? undefined : clip.id}
      data-vorab={vorab ? clip.id : undefined}
      data-maske={clip.maske?.form && clip.maske.form !== 'keine' ? clip.maske.form : undefined}
      playsInline
      preload={vorladen}
      disablePictureInPicture
      // Solange nichts dekodiert ist, steht das Standbild da -- dasselbe, das
      // hier früher für immer stand.
      poster={asset.posterUrl ?? undefined}
      onSeeking={merkeSuchen}
      onSeeked={merkeGesucht}
      onLoadedMetadata={(e) => {
        const v = e.currentTarget;
        const want = Number(v.dataset.want ?? '0');
        v.currentTime = Number.isFinite(want) && want > 0 ? want : 0.001;
      }}
      style={{
        // `inset` und die volle Höhe kommen aus dem Stilblatt und gelten für
        // die Basisflächen; eine Ebene steht dort, wo ihr Kasten steht.
        inset: 'auto',
        position: 'absolute',
        width: '100%',
        height: 'auto',
        // Sichtbar auch dann, wenn die Leinwand malt: sie liegt deckend
        // darüber. Ein unsichtbares Video liefert auf iOS kaum Bilder (7.69f).
        opacity,
        filter: GL_GEWUENSCHT ? undefined : cssFilter(clip, true),
        background: 'transparent',
        clipPath: form.clipPath,
        ...ebenenLage({ x, y, scale, rotate }),
      }}
    />
    </>
  );
}

/**
 * How far a layer still is from its resting place, as a fraction of the frame.
 * The same 0.4 s and the same 8 % / 6 % as `overlayPosition` in the renderer:
 * a preview that eases differently is a preview that lies.
 */
export function slideOffset(
  animation: Clip['animation'],
  since: number,
): { dx: number; dy: number } {
  const duration = 0.4;
  if (animation === 'none' || since < 0 || since >= duration) return { dx: 0, dy: 0 };
  const left = 1 - since / duration;
  if (animation === 'slideLeft') return { dx: left * 0.08, dy: 0 };
  if (animation === 'slideRight') return { dx: -left * 0.08, dy: 0 };
  if (animation === 'slideUp') return { dx: 0, dy: left * 0.06 };
  return { dx: 0, dy: -left * 0.06 };
}

/**
 * Eine Textebene. Gemerkt, aus demselben Grund wie der Look: ihre Angaben
 * ändern sich, wenn jemand am Text dreht, und nicht sechzigmal in der
 * Sekunde (7.27). Das `style`-Objekt kommt aus dem Schnitt und bleibt
 * dasselbe, solange niemand es anfasst -- sonst hülfe das Merken nichts.
 */
const TextLayer = memo(function TextLayer({
  clipId,
  text,
  style,
  x,
  y,
  rotate = 0,
  scale = 1,
  opacity = 1,
  height,
}: {
  clipId?: string;
  text: string;
  style: TextStyle;
  x: number;
  y: number;
  rotate?: number;
  scale?: number;
  opacity?: number;
  height: number;
}): ReactElement {
  return (
    <div
      className="text-layer"
      data-clip={clipId}
      style={{
        ...ebenenLage({ x, y, scale, rotate }),
        // The renderer sizes text as a fraction of the frame height, so the
        // preview has to do the same or the two disagree at export time.
        fontSize: `calc(var(--canvas-height, ${height}px) * ${style.size})`,
        color: style.color,
        // `paint-order` puts the stroke behind the glyph, which is what
        // ffmpeg's borderw does; without it the stroke eats into the letters.
        WebkitTextStroke: style.outlineColor
          ? `calc(var(--canvas-height, ${height}px) * ${style.size * style.outlineWidth}) ${style.outlineColor}`
          : undefined,
        paintOrder: 'stroke fill',
        fontWeight: style.bold ? 700 : 400,
        textAlign: style.align,
        textShadow: style.shadow ? '0 2px 6px #000c' : undefined,
        background: style.boxColor ? hexWithAlpha(style.boxColor, style.boxOpacity) : undefined,
        padding: style.boxColor ? '0.2em 0.45em' : undefined,
        borderRadius: style.boxColor ? 6 : undefined,
        opacity: GL_GEWUENSCHT ? 0 : opacity,
        maxWidth: '86%',
      }}
    >
      {style.uppercase ? text.toUpperCase() : text}
    </div>
  );
});

/**
 * Eine Bauchbinde im Bild. Ihre Teile stehen im Bezug zum ganzen Bild --
 * deshalb ist die Ebene bildfüllend --, aber die Ebene selbst liegt dort, wo
 * der Clip sie hinschiebt: Mitte auf x/y, dann skaliert und gedreht. Genau so
 * rechnet auch der Export (`overlayPosition` setzt die Mitte auf x/y,
 * `scale`/`rotate` davor), sonst zeigte die Vorschau etwas anderes als die
 * Datei.
 */
/** Breite durch Höhe des Films -- dieselbe Zahl, mit der der Export rechnet. */
function filmSeiten(): number {
  const { width, height } = useEditor.getState().timeline.settings;
  return height > 0 ? width / height : 1;
}

/** Der Kasten um den Inhalt einer Markenebene (7.19a), wie der Export ihn schätzt. */
function markenKastenVon(clip: Clip, brandKit: BrandKit | undefined): MarkenKasten | null {
  if (clip.source.kind !== 'brand') return null;
  const tpl = brandKit?.templates.find((t) => t.id === (clip.source as { templateId: string }).templateId);
  if (!tpl) return null;
  return markenKasten(tpl, clip.source.fields, filmSeiten(), Boolean(brandKit?.logoPath));
}

/**
 * Wohin die Mitte des *bildfüllenden* Markenbildes kommt, damit Grösse und
 * Drehung um die Mitte des Inhalts wirken (7.19a) -- für die Leinwand, die
 * das ganze Bild malt. Der Export rechnet dasselbe (`overlayPosition`).
 */
function markenMitte(
  clip: Clip,
  brandKit: BrandKit | undefined,
  lage: { x: number; y: number; scale: number; rotate: number },
): { x: number; y: number } {
  const k = markenKastenVon(clip, brandKit);
  if (!k) return { x: lage.x, y: lage.y };
  return markenLage(lage.x, lage.y, lage.scale, lage.rotate, markenVersatz(k), filmSeiten());
}

function BrandLayer({
  clip,
  opacity,
  brandKit,
  logoUrl,
  height,
  x = 0.5,
  y = 0.5,
  scale = 1,
  rotate = 0,
}: {
  clip: Clip;
  opacity: number;
  brandKit: BrandKit | undefined;
  logoUrl: string | null;
  height: number;
  x?: number;
  y?: number;
  scale?: number;
  rotate?: number;
}): ReactElement | null {
  if (clip.source.kind !== 'brand') return null;
  const template = brandKit?.templates.find((t) => t.id === (clip.source as { templateId: string }).templateId);
  if (!template) return null;
  const fields = clip.source.fields;
  // Die Ebene ist so gross wie ihr Inhalt, nicht wie das Bild (7.19a): der
  // Rahmen der Anfasser misst dieses Element, und gedreht wird um seine Mitte.
  // Die Teile stehen darin relativ zum Kasten; Schriftgrössen hängen weiter an
  // der Bildhöhe.
  const k = markenKastenVon(clip, brandKit) ?? { x: 0, y: 0, w: 1, h: 1 };
  const { dx, dy } = markenVersatz(k);
  const px = (wert: number) => `${((wert - k.x) / k.w) * 100}%`;
  const py = (wert: number) => `${((wert - k.y) / k.h) * 100}%`;
  const breit = (wert: number) => `${(wert / k.w) * 100}%`;

  return (
    <div
      data-clip={clip.id}
      data-marken-kasten={`${k.x.toFixed(4)} ${k.y.toFixed(4)} ${k.w.toFixed(4)} ${k.h.toFixed(4)}`}
      style={{
        position: 'absolute',
        width: `${k.w * 100}%`,
        height: `${k.h * 100}%`,
        opacity: GL_GEWUENSCHT ? 0 : opacity,
        ...ebenenLage({ x: x + dx, y: y + dy, scale, rotate }),
      }}
    >
      {template.layers.map((layer, index) => {
        if (layer.type === 'rect') {
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: px(layer.x),
                top: py(layer.y),
                width: breit(layer.w),
                height: `${(layer.h / k.h) * 100}%`,
                background: resolveBrandColor(brandKit ?? { primary: '#e11d48', secondary: '#0f172a' }, layer.color),
                opacity: layer.opacity,
              }}
            />
          );
        }
        if (layer.type === 'logo') {
          if (!logoUrl) return null;
          return (
            <img
              key={index}
              src={logoUrl}
              alt=""
              style={{
                position: 'absolute',
                left: px(layer.x),
                top: py(layer.y),
                width: breit(layer.w),
                opacity: layer.opacity,
              }}
            />
          );
        }
        if (layer.type === 'text') {
          const value = fields[layer.slot] ?? layer.placeholder;
          if (!value.trim()) return null;
          const align = layer.style.align;
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: px(layer.x),
                top: py(layer.y),
                // Sonst bräche der Text am Rand des Kastens um statt an seiner
                // eigenen Breite -- vorher war der Rand das Bild.
                width: 'max-content',
                maxWidth: breit(layer.maxWidth),
                transform: align === 'center' ? 'translateX(-50%)' : align === 'right' ? 'translateX(-100%)' : undefined,
                fontSize: `calc(var(--canvas-height, ${height}px) * ${layer.style.size})`,
                color: layer.color
                  ? resolveBrandColor(brandKit ?? { primary: '#e11d48', secondary: '#0f172a' }, layer.color)
                  : layer.style.color,
                fontWeight: layer.style.bold ? 700 : 400,
                textAlign: align,
                whiteSpace: 'pre-wrap',
              }}
            >
              {layer.style.uppercase ? value.toUpperCase() : value}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

/**
 * Die Medienzeit eines Clips *jetzt* -- aus dem genauen Kopf, nicht aus dem
 * zusammengefassten der Leinwand (7.69i).
 *
 * Seit 7.57d bekommt die Vorschau den Kopf nur noch, wenn sich am Bild etwas
 * ändert, im Takt von 200 ms. Für das Bild ist das richtig. Für den Abgleich
 * eines laufenden Videos war es falsch: `mediaTime` aus den Props hinkte bis
 * zu 200 ms hinterher, der Abgleich hielt das für Drift und stellte das
 * Tempo nach -- fünfmal je Sekunde rauf und runter. Auf dem iPhone ist jeder
 * Tempowechsel ein Ruckler. Die Basisspur merkte davon nichts, weil sie den
 * Takt angibt und nie korrigiert wird; die Ebene darüber ruckelte (Sascha,
 * 8. September, „Anno Domini“: „das eine Video flüssig, das andere auf
 * andere Ebene nicht“). Gemessen im Prüfstand: acht Tempowechsel in
 * anderthalb Sekunden.
 *
 * Dieselbe Formel wie in `visibleOverlays`/`visibleBase`.
 */
/**
 * Das Tempo eines Clips **am Abspielkopf**. Mit einer Rampe (S6) ändert es
 * sich über den Clip; ein Element, das mit dem Anfangstempo weiterliefe, wäre
 * nach einer Sekunde woanders als das Bild.
 */
function tempoJetzt(clip: Clip): number {
  const r = rampeVon(clip);
  if (r.von === r.bis) return r.von;
  const tau = Math.min(clip.duration, Math.max(0, useEditor.getState().playhead - clip.start));
  return r.von + ((r.bis - r.von) * tau) / clip.duration;
}

function medienzeitJetzt(clip: Clip): number {
  // Mit einer Rampe (S6) ist der Weg durch die Quelle die Fläche unter der
  // Tempokurve, nicht Zeit mal Zahl. Ohne Rampe rechnet `quellZeit` dasselbe
  // wie vorher.
  const tau = Math.max(0, useEditor.getState().playhead - clip.start);
  return clip.sourceIn + quellZeit(tau, clip.duration, rampeVon(clip));
}

/**
 * Der laufende Übergang, so wie die Leinwand ihn braucht (7.147).
 *
 * Die Zahl ist die Stelle in `UEBERGAENGE_MIT_FORM`, um eins nach oben --
 * 0 heisst „keine Form“, und genau das prüft der Shader.
 */
/**
 * Was die Leinwand vom laufenden Übergang wissen muss: die Form (0 heisst
 * keine) und, davon unabhängig, wie grob sie ablesen soll (7.148).
 *
 * Beides kann einzeln vorkommen. Eine Wischblende hat eine Form und kein
 * Raster; `pixelize` hat ein Raster und keine Form -- dort überblendet die
 * Deckung wie eh und je, nur die Abtaststelle wandert.
 */
function formUebergang(
  u: LaufenderUebergang | undefined,
  raster?: number,
): { art: number; p: number; raster?: number } | undefined {
  const art = u ? UEBERGAENGE_MIT_FORM.indexOf(u.art as (typeof UEBERGAENGE_MIT_FORM)[number]) + 1 : 0;
  if (art <= 0 && !(raster && raster > 0)) return undefined;
  return { art: Math.max(0, art), p: u?.p ?? 0, ...(raster && raster > 0 ? { raster } : {}) };
}

/** Look und Vignette eines Clips, so wie die Leinwand sie braucht (7.69c). */
function lookUndVignette(clip: Clip): {
  look?: { name: string; staerke: number };
  vignette?: number;
  blur?: number;
  raeder?: { tiefen: readonly number[]; mitten: readonly number[]; hoehen: readonly number[] };
  chroma?: { color: string; toleranz: number; weich: number };
} {
  const lut = lutOf(clip.effects);
  const raeder = raederOf(clip.effects);
  // Der Greenscreen (S11) -- bis zum 22. September kannte die Vorschau ihn
  // gar nicht (7.144).
  const chroma = clip.effects.find((e): e is Extract<typeof e, { type: 'chroma' }> => e.type === 'chroma');
  const vignette = vignetteAmount(clip);
  // Weichzeichnen wie im Export: `gblur=sigma=amount`, in Punkten des Films
  // (7.69g, 7.173); die Leinwand rechnet es auf die Punkte der Quelle um.
  const blur = clip.effects.find((e): e is Extract<typeof e, { type: 'blur' }> => e.type === 'blur');
  return {
    ...(lut ? { look: { name: lut.name, staerke: lut.intensity } } : {}),
    ...(vignette > 0 ? { vignette } : {}),
    ...(blur && blur.amount > 0 ? { blur: blur.amount } : {}),
    ...(raeder ? { raeder: { tiefen: raeder.tiefen, mitten: raeder.mitten, hoehen: raeder.hoehen } } : {}),
    ...(chroma ? { chroma: { color: chroma.color, toleranz: chroma.toleranz, weich: chroma.weich } } : {}),
  };
}

/** `#rrggbb` als drei Anteile 0..1, für die Grafikkarte. */
function farbeVon(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const voll = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.padEnd(6, '0');
  const n = parseInt(voll.slice(0, 6), 16);
  if (!Number.isFinite(n)) return [0, 0, 0];
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function hexWithAlpha(hex: string, alpha: number): string {
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
}

/**
 * The subtitle under the playhead, already broken into the lines the export
 * will use. Both halves of that matter: the burned-in file shifts every cue by
 * the transcript's own offset, and it wraps at `maxLineLength`. Without the
 * offset the preview showed the wrong sentence, and without the wrapping a
 * long one ran across the whole frame here and sat on three lines in the file.
 */
function useSubtitleAt(): { zeilen: string[]; betont: string[] | undefined } | null {
  const transcriptId = useEditor((s) => (s.timeline.subtitles.enabled ? s.timeline.subtitles.transcriptId : null));
  const maxLineLength = useEditor((s) => s.timeline.subtitles.maxLineLength);
  const toast = useEditor((s) => s.toast);
  const [cues, setCues] = useState<{ start: number; end: number; text: string; betont?: string[] }[]>([]);

  useEffect(() => {
    if (!transcriptId) {
      setCues([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.transcript(transcriptId);
        const { offset, segments } = res.transcript.data;
        if (!cancelled) {
          setCues(segments.map((s) => ({ start: s.start + offset, end: s.end + offset, text: s.text, betont: s.betont })));
        }
      } catch {
        if (!cancelled) toast('error', 'Das Transkript f\u00fcr die Untertitel lie\u00df sich nicht laden.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transcriptId, toast]);

  // Der Kopf wird hier selbst gelesen, und zwar genau -- nicht der
  // zusammengefasste aus `useBildKopf`. Ein Untertitel, der zweihundert
  // Millisekunden zu sp\u00e4t steht, steht sichtbar zu sp\u00e4t. Teuer ist das
  // nicht: der W\u00e4hler gibt einen Satz zur\u00fcck, und ein Satz \u00e4ndert sich
  // ein paarmal in der Minute.
  const cue = useEditor((s) => cues.find((c) => s.playhead >= c.start && s.playhead < c.end) ?? null);
  return useMemo(() => (!cue || cue.text === '' ? null : { zeilen: wrapText(cue.text, maxLineLength), betont: cue.betont }), [cue, maxLineLength]);
}

/**
 * Die Untertitelzeile.
 *
 * Ein eigenes Bauteil, seit die Leinwand beim Abspielen nicht mehr bei jedem
 * Bild durchl\u00e4uft: der Untertitel muss es, die Leinwand nicht. So zeichnet
 * sich hier neu, was sich \u00e4ndert, und dort bleibt es stehen.
 */
function Untertitel({
  subtitles,
  height,
}: {
  subtitles: Timeline['subtitles'];
  height: number;
}): ReactElement | null {
  zaehleBild('untertitel');
  const jetzt = useSubtitleAt();
  const zeilen = jetzt?.zeilen;
  if (!subtitles.enabled || !zeilen || zeilen.length === 0) return null;
  return (
    <div
      className="subtitle-layer"
      style={{
        top: `${subtitles.y * 100}%`,
        fontSize: `calc(var(--canvas-height, ${height}px) * ${subtitles.style.size})`,
        color: subtitles.style.color,
        fontWeight: subtitles.style.bold ? 700 : 400,
        textShadow: subtitles.style.shadow ? '0 2px 6px #000c' : undefined,
        // Off the scaled canvas, like the font size above -- against
        // the frame's own height the stroke came out as wide as the
        // letters on a phone.
        WebkitTextStroke: subtitles.style.outlineColor
          ? `calc(var(--canvas-height, ${height}px) * ${subtitles.style.size * subtitles.style.outlineWidth}) ${subtitles.style.outlineColor}`
          : undefined,
        paintOrder: 'stroke fill',
        background: subtitles.style.boxColor
          ? hexWithAlpha(subtitles.style.boxColor, subtitles.style.boxOpacity)
          : undefined,
        padding: subtitles.style.boxColor ? '0.15em 0.4em' : undefined,
        borderRadius: 6,
      }}
    >
      {zeilen.map((line, i) => (
        <div key={i}>
          {/* Schlagwörter hervorgehoben (4. November) -- dieselbe Regel wie im Export. */}
          {betonteStuecke(subtitles.style.uppercase ? line.toUpperCase() : line, jetzt?.betont).map((st, j) =>
            st.betont ? (
              <span key={j} data-betont style={{ color: subtitles.betonFarbe ?? '#ffd400', fontSize: `${subtitles.betonGroesse ?? 1.2}em` }}>
                {st.text}
              </span>
            ) : (
              <span key={j}>{st.text}</span>
            ),
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Eine Ebene an ihren Platz -- und zwar nur mit `transform`.
 *
 * Vorher stand die Stelle in `left`/`top` und die Groesse in `width`. Solange
 * nichts sich bewegt, ist das gleichgueltig. Sobald ein Keyframe laeuft, ist
 * es der Unterschied zwischen fluessig und stotternd: `left`, `top` und
 * `width` zwingen den Browser bei *jedem* Bild zu einem neuen Layout und
 * einem neuen Anstrich, `transform` und `opacity` nicht -- die bleiben beim
 * Compositor, und der sitzt auf der Grafikkarte. (Sascha, 8. September:
 * „sobald eine Keyframe Animation drin ist … stottert es wieder total“.)
 *
 * Der Kasten steht deshalb fest in der Mitte, und alles, was sich bewegt,
 * steckt in einer Zeichenkette:
 *
 *   translate(Leinwandanteil) scale() rotate() translate(-50%, -50%)
 *
 * Von rechts nach links gelesen: die Ebene wird auf ihren Ankerpunkt
 * zentriert, dann um ihn gedreht und skaliert (`transform-origin: 0 0` ist
 * genau dieser Punkt), dann in Bruchteilen der Leinwand verschoben. Die
 * Bruchteile brauchen die Masse der Leinwand -- ein Prozentwert in
 * `translate` bezoege sich auf das Element -- und die stehen als
 * `--canvas-width`/`--canvas-height` am Kasten.
 */
function ebenenLage(o: { x: number; y: number; scale: number; rotate: number }): CSSProperties {
  const dx = (o.x - 0.5).toFixed(5);
  const dy = (o.y - 0.5).toFixed(5);
  return {
    left: '50%',
    top: '50%',
    transformOrigin: '0 0',
    transform:
      `translate(calc(var(--canvas-width, 0px) * ${dx}), calc(var(--canvas-height, 0px) * ${dy}))` +
      ` scale(${o.scale}) rotate(${o.rotate}deg) translate(-50%, -50%)`,
  };
}

/**
 * Where the base video sits on the canvas: a box the size of the picture
 * (letterboxed inside the frame), moved, scaled and turned by the clip's
 * transform -- keyframes included -- exactly as the export composes it.
 * Percentages of the canvas, so the box follows every resize for free.
 */
function baseVideoBox(
  clip: Clip,
  playhead: number,
  dims: { w: number; h: number } | undefined,
  canvasAspect: number,
  schub = 0,
): CSSProperties {
  if (!dims) return {};
  const { w, h } = kastenAnteil(dims, canvasAspect);
  const tf = transformAt(clip, playhead - clip.start);
  // Ein Schiebe-Übergang rückt die ganze Ebene (7.148) -- auch ohne
  // Leinwand (7.161).
  return {
    inset: 'auto',
    width: `${w}%`,
    height: `${h}%`,
    background: 'transparent',
    ...ebenenLage({ ...tf, x: tf.x + schub }),
  };
}

/** Wie gross das Bild in der Leinwand steht, in Prozent ihrer Breite und Höhe. */
function kastenAnteil(dims: { w: number; h: number }, canvasAspect: number): { w: number; h: number } {
  const aspect = dims.w / dims.h;
  return {
    w: aspect >= canvasAspect ? 100 : (aspect / canvasAspect) * 100,
    h: aspect >= canvasAspect ? (canvasAspect / aspect) * 100 : 100,
  };
}

/**
 * Ist der Clip ein Standbild? Ein Bild bekommt beim Aufbereiten einen Poster,
 * aber keine Vorschau-Datei — es gehört deshalb nicht in ein `<video>`.
 */
function istStandbild(assets: Map<string, AssetView>, clip: Clip): boolean {
  if (clip.source.kind !== 'asset') return false;
  return assets.get(clip.source.assetId)?.kind === 'image';
}

/** The size the asset row knows, for the moment before the proxy has loaded. */
function assetDims(assets: Map<string, AssetView>, clip: Clip): { w: number; h: number } | undefined {
  if (clip.source.kind !== 'asset') return undefined;
  const a = assets.get(clip.source.assetId);
  return a && a.width > 0 && a.height > 0 ? { w: a.width, h: a.height } : undefined;
}
